import { useState, useEffect, useMemo, useRef, lazy, Suspense } from 'react';
import { Calendar, Users, User as UserIcon, Clock, Home, Coins, Key, UserPlus, LogOut, Menu, X, CreditCard, AlertTriangle, Flag, Share2 } from 'lucide-react';
import { CalendarView, refreshCalendarDays } from './components/CalendarView';
import { SessionTimer } from './components/SessionTimer';
import { GlobalSpinner } from './components/GlobalSpinner';
import { ThemeToggle } from './components/ThemeToggle';
import { LanguageSelector } from './components/LanguageSelector';
import { TrocarSenhaModal } from './components/TrocarSenhaModal';
// Test components removidos
import { useGlobalSpinner } from './hooks/useGlobalSpinner';
import { useDataRefreshTimer } from './hooks/useDataRefreshTimer';
import { useDropdownClasses } from './hooks/useDropdownLayout';
import { AuthService } from './services/AuthService';
import { ReservationService } from './services/ReservationService';
import { CheckoutService } from './services/CheckoutService';
import { getApiBaseUrl } from './config/api';
import { AuthModal } from './components/AuthModal';
import { User, Agenda } from './types';
import ApiStatusCheck from './components/ApiStatusCheck';
import { checkSlotAvailability, formatSlotTime } from './utils/slotAvailability';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { LanguageProvider, useLanguage } from './contexts/LanguageContext';
import { toastError, toastSuccess } from './utils/toast';
import { obterBaseAplicacaoAtual } from './utils/appBasePath';
import { isTokenExpired, limparAutenticacao, obterTokenAutenticacaoCliente } from './utils/authToken';

// {|INICIO| 02/04/2026 às 10:12 - BUILD: CODE-SPLITTING (LAZY LOAD) PARA REDUZIR BUNDLE INICIAL}
const EnhancedReservationModal = lazy(() =>
  import('./components/EnhancedReservationModal').then((mod) => ({ default: mod.EnhancedReservationModal })),
);
const EnhancedCheckoutModal = lazy(() =>
  import('./components/EnhancedCheckoutModal').then((mod) => ({ default: mod.EnhancedCheckoutModal })),
);
const EnhancedParticipantDashboard = lazy(() =>
  import('./components/EnhancedParticipantDashboard').then((mod) => ({ default: mod.EnhancedParticipantDashboard })),
);
const EnhancedInviteAcceptance = lazy(() =>
  import('./components/EnhancedInviteAcceptance').then((mod) => ({ default: mod.EnhancedInviteAcceptance })),
);
const MyReservations = lazy(() => import('./components/MyReservations').then((mod) => ({ default: mod.MyReservations })));
const MyRaces = lazy(() => import('./components/MyRaces').then((mod) => ({ default: mod.MyRaces })));
const ConsultarPontosModal = lazy(() =>
  import('./components/ConsultarPontosModal').then((mod) => ({ default: mod.ConsultarPontosModal })),
);
// {|INICIO| 23/07/2026 às 11:25 - ETAPA 7: REMOVER LAZY LOAD DO MODAL DE TROCA DE SENHA PARA EVITAR FALHA DE CHUNK}
// Histórico (versão anterior): `TrocarSenhaModal` era carregado via `lazy()` e podia falhar quando o navegador perdia o chunk publicado.
// O modal passa a fazer parte do bundle principal por ser um fluxo crítico de autenticação.
// {|FIM| 23/07/2026 às 11:25 - ETAPA 7: REMOVER LAZY LOAD DO MODAL DE TROCA DE SENHA PARA EVITAR FALHA DE CHUNK}
const NovoCadastroModal = lazy(() => import('./components/NovoCadastroModal').then((mod) => ({ default: mod.NovoCadastroModal })));
const MeuPerfilModal = lazy(() => import('./components/MeuPerfilModal').then((mod) => ({ default: mod.MeuPerfilModal })));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const PagarmeTestSuite = lazy(() => import('./components/PagarmeTestSuite'));
const WebhookMonitoringDashboard = lazy(() => import('./components/WebhookMonitoringDashboard'));
// {|FIM| 02/04/2026 às 10:12 - BUILD: CODE-SPLITTING (LAZY LOAD) PARA REDUZIR BUNDLE INICIAL}

// {|INICIO| 11/06/2026 às 20:47 - COMPARTILHAMENTO: ADICIONAR LINK PROFUNDO DA DATA SELECIONADA}
const PARAMETRO_DATA_COMPARTILHADA = 'data';

// Comentário (function-level): Converte a data selecionada para o formato estável da URL compartilhável.
const formatarDataParaParametroUrl = (dataSelecionada: Date): string => {
  const ano = dataSelecionada.getFullYear();
  const mes = String(dataSelecionada.getMonth() + 1).padStart(2, '0');
  const dia = String(dataSelecionada.getDate()).padStart(2, '0');
  return `${ano}-${mes}-${dia}`;
};

// {|INICIO| 21/06/2026 às 21:31 - DATA INICIAL: NORMALIZAR DATAS DA HOME PARA EVITAR DIVERGÊNCIA ENTRE CARGA INICIAL E CLIQUE NO CALENDÁRIO}
// Comentário (function-level): Remove hora/minuto/segundo da data para garantir o mesmo comportamento entre abertura inicial, clique no calendário e compartilhamento.
const normalizarDataSemHorario = (dataReferencia: Date): Date => {
  return new Date(
    dataReferencia.getFullYear(),
    dataReferencia.getMonth(),
    dataReferencia.getDate()
  );
};
// {|FIM| 21/06/2026 às 21:31 - DATA INICIAL: NORMALIZAR DATAS DA HOME PARA EVITAR DIVERGÊNCIA ENTRE CARGA INICIAL E CLIQUE NO CALENDÁRIO}

// Comentário (function-level): Lê a data da query string e cria uma data local sem efeito colateral de fuso horário.
const converterParametroUrlParaData = (valorData: string | null): Date | null => {
  if (!valorData || !/^\d{4}-\d{2}-\d{2}$/.test(valorData)) return null;

  const [anoTexto, mesTexto, diaTexto] = valorData.split('-');
  const ano = Number(anoTexto);
  const mes = Number(mesTexto);
  const dia = Number(diaTexto);

  if (!Number.isInteger(ano) || !Number.isInteger(mes) || !Number.isInteger(dia)) return null;

  const dataConvertida = new Date(ano, mes - 1, dia);
  const dataValida =
    dataConvertida.getFullYear() === ano &&
    dataConvertida.getMonth() === mes - 1 &&
    dataConvertida.getDate() === dia;

  return dataValida ? dataConvertida : null;
};

// Comentário (function-level): Inicializa a home já na data compartilhada quando o usuário abre um link profundo.
const obterDataInicialCompartilhada = (): Date => {
  const parametrosUrl = new URLSearchParams(window.location.search);
  // {|INICIO| 21/06/2026 às 21:31 - DATA INICIAL: PADRONIZAR DATA DA HOME SEM HORÁRIO}
  // Versão anterior (histórico): retornava `new Date()` e mantinha o horário atual na primeira carga da home.
  // return converterParametroUrlParaData(parametrosUrl.get(PARAMETRO_DATA_COMPARTILHADA)) ?? new Date();
  return normalizarDataSemHorario(
    converterParametroUrlParaData(parametrosUrl.get(PARAMETRO_DATA_COMPARTILHADA)) ?? new Date()
  );
  // {|FIM| 21/06/2026 às 21:31 - DATA INICIAL: PADRONIZAR DATA DA HOME SEM HORÁRIO}
};

// Comentário (function-level): Monta o link público da home preservando a URL atual e atualizando apenas a data compartilhada.
const montarLinkCompartilhamentoData = (dataSelecionada: Date): string => {
  const urlCompartilhamento = new URL(window.location.href);
  urlCompartilhamento.searchParams.delete('invite');
  urlCompartilhamento.searchParams.set(PARAMETRO_DATA_COMPARTILHADA, formatarDataParaParametroUrl(dataSelecionada));
  return urlCompartilhamento.toString();
};

// Comentário (function-level): Remove apenas o parâmetro de data compartilhada da URL atual sem afetar os demais parâmetros.
const removerParametroDataCompartilhadaDaUrl = (): void => {
  const urlAtual = new URL(window.location.href);
  if (!urlAtual.searchParams.has(PARAMETRO_DATA_COMPARTILHADA)) return;

  urlAtual.searchParams.delete(PARAMETRO_DATA_COMPARTILHADA);
  const novoCaminho = `${urlAtual.pathname}${urlAtual.search}${urlAtual.hash}`;
  window.history.replaceState({}, '', novoCaminho);
};

// Comentário (function-level): Copia texto para a área de transferência usando API moderna e fallback compatível com navegadores antigos.
const copiarTextoParaAreaTransferencia = async (texto: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(texto);
      return true;
    }
  } catch {
    // Continua para o fallback abaixo.
  }

  try {
    const areaTextoTemporaria = document.createElement('textarea');
    areaTextoTemporaria.value = texto;
    areaTextoTemporaria.setAttribute('readonly', 'true');
    areaTextoTemporaria.style.position = 'fixed';
    areaTextoTemporaria.style.opacity = '0';
    document.body.appendChild(areaTextoTemporaria);
    areaTextoTemporaria.select();
    const copiou = document.execCommand('copy');
    document.body.removeChild(areaTextoTemporaria);
    return copiou;
  } catch {
    return false;
  }
};
// {|FIM| 11/06/2026 às 20:47 - COMPARTILHAMENTO: ADICIONAR LINK PROFUNDO DA DATA SELECIONADA}

function AppContent() {
  const { isLoading: globalLoading } = useGlobalSpinner();
  // {|INICIO| 06/05/2026 às 16:05 - I18N: OBTER language PARA SELEÇÃO DE TEXTO DO BACKEND E LABELS DO MENU}
  const { t, language } = useLanguage();
  // {|FIM| 06/05/2026 às 16:05 - I18N: OBTER language PARA SELEÇÃO DE TEXTO DO BACKEND E LABELS DO MENU}
  const { theme } = useTheme();
  // Comentário (function-level): Centraliza o estilo visual do container da logo para garantir contraste consistente entre tema claro e escuro.
  const estiloContainerLogo = theme === 'dark'
    ? {
        background: 'linear-gradient(90deg, #1e293b 0%, #0f172a 55%, #020617 100%)',
        borderColor: '#475569'
      }
    : {
        background: 'linear-gradient(90deg, #7f1d1d 0%, #991b1b 55%, #881337 100%)',
        borderColor: '#fecaca'
      };
  const { mobileMenuClass, desktopMenuClass, shouldUseDropdown } = useDropdownClasses();
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(() => obterDataInicialCompartilhada());
  const referenciaJaConsumiuDataCompartilhada = useRef<boolean>(window.location.search.includes(`${PARAMETRO_DATA_COMPARTILHADA}=`));
  const [availableSlots, setAvailableSlots] = useState<Agenda[]>([]);
  // {|INICIO| 02/04/2026 às 10:42 - BANNER: INFORMAR % DE DESCONTO NA TAXA DE PROCESSAMENTO (CONFORME PARTICIPANTES)}
  // {|INICIO| 02/04/2026 às 12:44 - BANNER: BUSCAR SEMPRE DO BACKEND (TADMIN) E NÃO PERSISTIR EM STORAGE}
  // Versão anterior (histórico): lia e persistia em localStorage para reaproveitar a última cotação do checkout.
  // const lerInfoDescontoTaxaProcessamentoDoStorage = (): { percentual: string | null; quantidadeMinimaParticipantes: number | null } => {
  //   try {
  //     const percentualRaw = (typeof localStorage !== 'undefined' ? localStorage.getItem('processingFeeDiscountPct') : null) ?? null;
  //     const percentualTexto = percentualRaw && percentualRaw.trim() ? percentualRaw.trim() : null;
  //     const percentualNumero = percentualTexto != null ? Number(percentualTexto.replace(',', '.')) : NaN;
  //     const percentual = Number.isFinite(percentualNumero) && percentualNumero > 0 ? percentualTexto : null;
  //     const quantidadeMinimaRaw = (typeof localStorage !== 'undefined' ? localStorage.getItem('processingFeeDiscountMinParticipants') : null) ?? null;
  //     const quantidadeMinimaParsed = quantidadeMinimaRaw != null && quantidadeMinimaRaw.trim() ? Number(quantidadeMinimaRaw) : NaN;
  //     const quantidadeMinimaParticipantes = Number.isFinite(quantidadeMinimaParsed) && quantidadeMinimaParsed > 0 ? quantidadeMinimaParsed : null;
  //     return { percentual, quantidadeMinimaParticipantes };
  //   } catch {
  //     return { percentual: null, quantidadeMinimaParticipantes: null };
  //   }
  // };
  // Function-level comment: Busca do backend (flags/runtime) os parâmetros do desconto configurados no tadmin.
  const extrairInfoDescontoTaxaProcessamentoDoBackend = async (): Promise<{ percentual: string | null; quantidadeMinimaParticipantes: number | null }> => {
    try {
      // {|INICIO| 02/04/2026 às 13:15 - BANNER: FALLBACK PARA ENDPOINT DEDICADO QUANDO ping NÃO EXPÕE O DESCONTO}
      const asRecord = (v: unknown): Record<string, unknown> | null => (v && typeof v === 'object' ? (v as Record<string, unknown>) : null);

      const extrairDeRecord = (record: Record<string, unknown> | null): { percentual: string | null; quantidadeMinimaParticipantes: number | null } => {
        if (!record) return { percentual: null, quantidadeMinimaParticipantes: null };
        const lerValor = (chaves: string[]): unknown => {
          for (const chave of chaves) {
            if (Object.prototype.hasOwnProperty.call(record, chave)) return (record as Record<string, unknown>)[chave];
          }
          return null;
        };

        const parseNumero = (valor: unknown): number | null => {
          if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null;
          if (typeof valor === 'string') {
            const normalizado = valor.trim().replace(',', '.');
            const numero = Number(normalizado);
            return Number.isFinite(numero) ? numero : null;
          }
          return null;
        };

        const percentualNumero = parseNumero(lerValor([
          'processingFeeDiscountPct',
          'processing_fee_discount_pct',
          'desconto_taxa_processamento_percent',
          'desconto_taxa_processamento_pct',
          'desconto_taxa_processamento_percentual'
        ]));
        const percentualValido = percentualNumero != null && percentualNumero > 0 ? Math.min(100, Math.max(0, percentualNumero)) : null;
        const percentualString = percentualValido == null
          ? null
          : (() => {
              const arredondado = Math.round(percentualValido);
              return Math.abs(percentualValido - arredondado) <= 0.05 ? String(arredondado) : String(Number(percentualValido.toFixed(1)));
            })();

        const quantidadeMinimaNumero = parseNumero(lerValor([
          'processingFeeDiscountMinParticipants',
          'processing_fee_discount_min_participants',
          'qtd_min_part_desc_taxa_processamento',
          'min_participants_processing_fee_discount',
          'processing_fee_discount_min_participants'
        ]));
        const quantidadeMinimaParticipantes = quantidadeMinimaNumero != null && Number.isFinite(quantidadeMinimaNumero) && quantidadeMinimaNumero > 0
          ? Math.round(quantidadeMinimaNumero)
          : null;

        return { percentual: percentualString, quantidadeMinimaParticipantes };
      };

      const flags = await CheckoutService.getFrontendFlags({ forcarAtualizacao: true });
      const flagsRec = asRecord(flags);
      const infoViaFlags = extrairDeRecord(flagsRec);
      if (infoViaFlags.percentual) return infoViaFlags;

      const configDesconto = await CheckoutService.getProcessingFeeDiscountConfig();
      return extrairDeRecord(asRecord(configDesconto));
      // {|FIM| 02/04/2026 às 13:15 - BANNER: FALLBACK PARA ENDPOINT DEDICADO QUANDO ping NÃO EXPÕE O DESCONTO}
    } catch {
      return { percentual: null, quantidadeMinimaParticipantes: null };
    }
  };
  const [infoDescontoTaxaProcessamento, setInfoDescontoTaxaProcessamento] = useState<{ percentual: string | null; quantidadeMinimaParticipantes: number | null }>({
    percentual: null,
    quantidadeMinimaParticipantes: null
  });
  useEffect(() => {
    let ativo = true;
    const carregarInfo = async () => {
      const infoBackend = await extrairInfoDescontoTaxaProcessamentoDoBackend();
      if (!ativo) return;
      setInfoDescontoTaxaProcessamento(infoBackend);
    };
    carregarInfo();
    const handleFocus = () => { void carregarInfo(); };
    const handleVisibility = () => { if (document.visibilityState === 'visible') void carregarInfo(); };
    const intervaloId = window.setInterval(() => { void carregarInfo(); }, 30000);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);
    try {
      localStorage.removeItem('processingFeeDiscountPct');
      localStorage.removeItem('processingFeeDiscountMinParticipants');
    } catch {}
    return () => {
      ativo = false;
      window.clearInterval(intervaloId);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, []);
  const [temAvisoDiaSelecionado, setTemAvisoDiaSelecionado] = useState(false);
  const textoAvisoTaxaProcessamento = useMemo(() => {
    // {|INICIO| 02/04/2026 às 16:35 - BANNER: VOLTAR A CONCATENAR AVISO BASE COM TEXTO DE DESCONTO QUANDO % > 0}
    // {|INICIO| 18/06/2026 às 09:18 - BANNER: OCULTAR COMPLEMENTO DE DESCONTO QUANDO EXISTIR MENSAGEM DO DIA}
    // {|INICIO| 18/06/2026 às 09:41 - BANNER: ANEXAR APENAS O TRECHO DE DESCONTO AO HTML PADRÃO SEM DUPLICAR O TEXTO BASE}
    // Versão anterior (histórico): exibia apenas o texto de desconto quando havia configuração no TAdmin.
    // const textoBase = t('app.processingFeeNote');
    // const percentual = infoDescontoTaxaProcessamento?.percentual;
    // if (percentual) {
    //   const quantidadeMinima = infoDescontoTaxaProcessamento?.quantidadeMinimaParticipantes;
    //   if (quantidadeMinima && quantidadeMinima > 0) {
    //     return t('app.processingFeeDiscountInfoWithMin', { pct: percentual, min: quantidadeMinima });
    //   }
    //   return t('app.processingFeeDiscountInfo', { pct: percentual });
    // }
    // return textoBase;

    const percentual = infoDescontoTaxaProcessamento?.percentual;
    const percentualNumero = percentual != null ? Number(String(percentual).trim().replace(',', '.')) : 0;
    const percentualValido = Number.isFinite(percentualNumero) && percentualNumero > 0;

    if (temAvisoDiaSelecionado) return '';
    if (!percentualValido) return '';

    const percentualTexto = String(percentual);
    const quantidadeMinima = infoDescontoTaxaProcessamento?.quantidadeMinimaParticipantes;
    if (quantidadeMinima && quantidadeMinima > 0) {
      return t('app.processingFeeDiscountInfoWithMin', { pct: percentualTexto, min: quantidadeMinima });
    }
    return t('app.processingFeeDiscountInfo', { pct: percentualTexto });
    // {|FIM| 18/06/2026 às 09:18 - BANNER: OCULTAR COMPLEMENTO DE DESCONTO QUANDO EXISTIR MENSAGEM DO DIA}
    // {|FIM| 18/06/2026 às 09:41 - BANNER: ANEXAR APENAS O TRECHO DE DESCONTO AO HTML PADRÃO SEM DUPLICAR O TEXTO BASE}
    // {|FIM| 02/04/2026 às 16:35 - BANNER: VOLTAR A CONCATENAR AVISO BASE COM TEXTO DE DESCONTO QUANDO % > 0}
  }, [infoDescontoTaxaProcessamento, t, temAvisoDiaSelecionado]);
  // {|FIM| 02/04/2026 às 10:42 - BANNER: INFORMAR % DE DESCONTO NA TAXA DE PROCESSAMENTO (CONFORME PARTICIPANTES)}
  // {|FIM| 02/04/2026 às 12:44 - BANNER: BUSCAR SEMPRE DO BACKEND (TADMIN) E NÃO PERSISTIR EM STORAGE}
  // {|INICIO| 27/04/2026 às 00:25 - BANNER: CARREGAR HTML SEGURO DO AVISO VERMELHO VIA API (RESERVA ONLINE)}
  const [htmlAvisoReservaOnline, setHtmlAvisoReservaOnline] = useState<string>('');
  // {|INICIO| 06/05/2026 às 16:05 - I18N: SELECIONAR TRECHO DO TEXTO DO BACKEND POR IDIOMA ([BR]/[EN]/[ES])}
  const selecionarHtmlPorIdioma = (html: string, idioma: 'pt' | 'en' | 'es'): string => {
    /**
     * Comentário (function-level): Permite armazenar no backend um único campo HTML com seções por idioma.
     * Formato suportado (exemplo):
     * [BR]...html em pt...
     * [EN]...html em en...
     * [ES]...html em es...
     *
     * Fallback:
     * - Se não houver marcadores, retorna o HTML original.
     * - Se o idioma não existir, cai para BR/PT, ou para a primeira seção encontrada.
     */
    const texto = String(html || '');
    const marcador = /\[(BR|PT|EN|ES)\]/g;
    const matches = Array.from(texto.matchAll(marcador));
    if (!matches.length) return texto;

    const secoes: Record<string, string> = {};
    for (let i = 0; i < matches.length; i++) {
      const sigla = String(matches[i][1] || '').toUpperCase();
      const inicioConteudo = (matches[i].index ?? 0) + matches[i][0].length;
      const fimConteudo = (i + 1 < matches.length) ? (matches[i + 1].index ?? texto.length) : texto.length;
      const conteudo = texto.slice(inicioConteudo, fimConteudo).trim();
      if (sigla) secoes[sigla] = conteudo;
    }

    const chavePreferida = idioma === 'pt' ? 'BR' : idioma.toUpperCase();
    const candidato = secoes[chavePreferida] || (idioma === 'pt' ? secoes['PT'] : '');
    if (typeof candidato === 'string' && candidato.trim()) return candidato;

    const fallbackPt = secoes['BR'] || secoes['PT'];
    if (typeof fallbackPt === 'string' && fallbackPt.trim()) return fallbackPt;

    const primeiraSecao = Object.values(secoes).find((v) => typeof v === 'string' && v.trim());
    return (typeof primeiraSecao === 'string' && primeiraSecao.trim()) ? primeiraSecao : texto;
  };

  const htmlAvisoReservaOnlinePorIdioma = useMemo(() => {
    return selecionarHtmlPorIdioma(htmlAvisoReservaOnline, language);
  }, [htmlAvisoReservaOnline, language]);
  // {|INICIO| 18/06/2026 às 09:41 - BANNER: ANEXAR COMPLEMENTO DE DESCONTO À ÚLTIMA LINHA DO HTML PADRÃO}
  /**
   * Comentário (function-level): Anexa o complemento local de desconto ao final do último parágrafo do HTML padrão,
   * preservando a mensagem do dia sem complemento e evitando criar um novo bloco visual no banner.
   */
  const htmlAvisoReservaOnlineRenderizado = useMemo(() => {
    const htmlBase = String(htmlAvisoReservaOnlinePorIdioma || '').trim();
    const complemento = String(textoAvisoTaxaProcessamento || '').trim();

    if (!htmlBase) return '';
    if (!complemento) return htmlBase;

    if (/<\/p>\s*$/i.test(htmlBase)) {
      return htmlBase.replace(/<\/p>\s*$/i, ` ${complemento}</p>`);
    }

    return `${htmlBase} ${complemento}`;
  }, [htmlAvisoReservaOnlinePorIdioma, textoAvisoTaxaProcessamento]);
  // {|INICIO| 18/06/2026 às 11:06 - BANNER: REMOVER LOGS TEMPORÁRIOS DE DEBUG DO HTML FINAL DO AVISO}
  // Instrumentação temporária removida após validar no ambiente real a renderização correta do aviso por data.
  // {|FIM| 18/06/2026 às 11:06 - BANNER: REMOVER LOGS TEMPORÁRIOS DE DEBUG DO HTML FINAL DO AVISO}
  // {|FIM| 18/06/2026 às 09:41 - BANNER: ANEXAR COMPLEMENTO DE DESCONTO À ÚLTIMA LINHA DO HTML PADRÃO}
  // {|FIM| 06/05/2026 às 16:05 - I18N: SELECIONAR TRECHO DO TEXTO DO BACKEND POR IDIOMA ([BR]/[EN]/[ES])}
  // {|INICIO| 16/06/2026 às 21:22 - BANNER: RECARREGAR AVISO CONFORME A DATA SELECIONADA}
  // Comentário (function-level): Converte a data selecionada para yyyy-mm-dd em horário local, evitando desvio de fuso ao consultar avisos específicos do dia.
  const formatarDataAvisoSelecionada = (dataSelecionada: Date): string => {
    const ano = dataSelecionada.getFullYear();
    const mes = String(dataSelecionada.getMonth() + 1).padStart(2, '0');
    const dia = String(dataSelecionada.getDate()).padStart(2, '0');
    return `${ano}-${mes}-${dia}`;
  };

  // Comentário (function-level): Busca o HTML do aviso vermelho (sanitizado no backend) e atualiza quando a data da agenda muda ou a aba volta ao foco.
  useEffect(() => {
    let ativo = true;

    const carregarHtmlAviso = async () => {
      const dataAvisoSelecionada = formatarDataAvisoSelecionada(selectedDate);
      const retornoAviso = await ReservationService.getTextoAvisoReservaOnlineHtml(1, dataAvisoSelecionada);
      if (!ativo) return;
      setHtmlAvisoReservaOnline(retornoAviso.html);
      setTemAvisoDiaSelecionado(retornoAviso.temAvisoDia);
    };

    void carregarHtmlAviso();
    const handleFocus = () => { void carregarHtmlAviso(); };
    const handleVisibility = () => { if (document.visibilityState === 'visible') void carregarHtmlAviso(); };
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      ativo = false;
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [selectedDate]);
  // {|FIM| 16/06/2026 às 21:22 - BANNER: RECARREGAR AVISO CONFORME A DATA SELECIONADA}
  // {|FIM| 27/04/2026 às 00:25 - BANNER: CARREGAR HTML SEGURO DO AVISO VERMELHO VIA API (RESERVA ONLINE)}
  // {|INICIO| 12/03/2026 às 21:05 - UI: ADICIONAR TOGGLE PARA OCULTAR HORÁRIOS INDISPONÍVEIS}
  const [hideUnavailableSlots, setHideUnavailableSlots] = useState(false);
  const slotsToRender = useMemo(() => {
    // {|INICIO| 28/03/2026 às 00:24 - AGENDA: USAR DATA SELECIONADA NO CÁLCULO DE INDISPONIBILIDADE}
    // Versão anterior (histórico):
    // return availableSlots.filter((slot) => !checkSlotAvailability(slot).isDisabled);
    if (!hideUnavailableSlots) return availableSlots;
    return availableSlots.filter((slot) => !checkSlotAvailability(slot, undefined, selectedDate).isDisabled);
    // {|FIM| 28/03/2026 às 00:24 - AGENDA: USAR DATA SELECIONADA NO CÁLCULO DE INDISPONIBILIDADE}
  }, [availableSlots, hideUnavailableSlots, selectedDate]);
  // {|FIM| 12/03/2026 às 21:05 - UI: ADICIONAR TOGGLE PARA OCULTAR HORÁRIOS INDISPONÍVEIS}
  // {|INICIO| 29/03/2026 às 20:00 - AGENDA: IDENTIFICAR BATERIA ABERTA NOS SLOTS}
  /**
   * Identifica se o slot está com Bateria Aberta (não fechado e com vagas disponíveis).
   */
  const isOpenBatterySlot = (slot: Agenda): boolean => {
    // {|INICIO| 29/03/2026 às 22:56 - AGENDA: EXIBIR "BATERIA ABERTA" TAMBÉM QUANDO HÁ RESERVAS PARCIAIS}
    // Versão anterior (histórico): só marcava como aberta quando todas as vagas estavam disponíveis
    // const total = Number(slot.vagas_totais || 0);
    // const vagas = Number(slot.vagas || 0);
    // return Number.isFinite(total) && total > 0 && vagas === total && !Boolean(slot.fechado);

    const vagas = Number((slot as unknown as { vagas?: unknown })?.vagas ?? 0);
    const fechadoRaw = (slot as unknown as { fechado?: unknown })?.fechado;
    const isFechado = fechadoRaw === true || fechadoRaw === 1 || fechadoRaw === '1';

    return Number.isFinite(vagas) && vagas > 0 && !isFechado;
    // {|FIM| 29/03/2026 às 22:56 - AGENDA: EXIBIR "BATERIA ABERTA" TAMBÉM QUANDO HÁ RESERVAS PARCIAIS}
  };
  // {|FIM| 29/03/2026 às 20:00 - AGENDA: IDENTIFICAR BATERIA ABERTA NOS SLOTS}
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showReservationModal, setShowReservationModal] = useState(false);
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  const [showMyReservations, setShowMyReservations] = useState(false);
  const [showMyRaces, setShowMyRaces] = useState(false);
  const [showParticipantDashboard, setShowParticipantDashboard] = useState(false);
  const [showConsultarPontos, setShowConsultarPontos] = useState(false);
  const [showTrocarSenha, setShowTrocarSenha] = useState(false);
  const [showNovoCadastro, setShowNovoCadastro] = useState(false);
  const [showMeuPerfil, setShowMeuPerfil] = useState(false);
  const [showPagarmeTest, setShowPagarmeTest] = useState(false);
  const [showWebhookMonitoring, setShowWebhookMonitoring] = useState(false);
  const [selectedAgenda, setSelectedAgenda] = useState<Agenda | null>(null);
  // {|INICIO| 11/06/2026 às 21:40 - AGENDA: SUAVIZAR REFRESH MANTENDO LISTA VISÍVEL DURANTE ATUALIZAÇÃO}
  const [agendaJaCarregada, setAgendaJaCarregada] = useState(false);
  const [carregandoAgendaInicial, setCarregandoAgendaInicial] = useState(true);
  const [atualizandoAgendaSilenciosamente, setAtualizandoAgendaSilenciosamente] = useState(false);
  // {|FIM| 11/06/2026 às 21:40 - AGENDA: SUAVIZAR REFRESH MANTENDO LISTA VISÍVEL DURANTE ATUALIZAÇÃO}
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  // Estados de testes removidos

  // Flags de controle de funcionalidades
  const enablePagarmeTest = import.meta.env.VITE_ENABLE_PAGARME_TEST === 'true';
  const enableWebhookMonitoring = import.meta.env.VITE_ENABLE_WEBHOOK_MONITORING === 'true';

  // Check for invite token in URL
  const urlParams = new URLSearchParams(window.location.search);
  const inviteToken = urlParams.get('invite');
  const resetPasswordToken = urlParams.get('token');

  // Auth initialization and event listeners
  useEffect(() => {
    const userData = AuthService.getCurrentUser();
    const tokenAutenticacaoCliente = obterTokenAutenticacaoCliente();
    const existeSessaoPersistida = Boolean(userData || tokenAutenticacaoCliente);
    const tokenAutenticacaoExpirado = tokenAutenticacaoCliente ? isTokenExpired() : false;
    const estaNoFluxoDeConvite = Boolean(inviteToken);

    // {|INICIO| 22/07/2026 às 15:55 - CONVITE: EVITAR ALERTA FALSO DE SESSAO EXPIRADA AO ABRIR LINK DESLOGADO}
    // Versão anterior (histórico): tratava ausência de token como sessão expirada e disparava logout forçado no boot.
    // if (isTokenExpired()) {
    //   limparAutenticacao(true);
    // }
    if (tokenAutenticacaoExpirado) {
      limparAutenticacao(true);
    }

    // Helper to fetch complete user data including address fields
    const fetchCompleteUserData = async (userId: number) => {
      try {
        const response = await ReservationService.getCliente(userId);
        if (response.status === 'Sucesso' && response.data) {
          const userData = response.data as any;
          const usuarioAtual = AuthService.getCurrentUser();
          
          // Normalize location data (IBGE codes vs names)
          const codigoUfCliente = (userData.codigo_uf_cliente ?? userData.codigo_uf ?? userData.uf ?? '').toString();
          const codigoIbgeCidadeCliente = (userData.codigo_ibge_cidade_cliente ?? userData.codigo_ibge_cidade ?? '').toString();
          
          const completeUser: User = {
              ...(usuarioAtual || {}),
              ...userData,
              id: userData.id || userId,
              idcliente: userData.idcliente || userData.id || userId, // Garantir idcliente
              estado: codigoUfCliente || userData.estado,
              cidade: codigoIbgeCidadeCliente || userData.cidade,
          };
          
          setCurrentUser(completeUser);
        }
      } catch (error) {
        console.error('Failed to fetch complete user profile', error);
      }
    };

    // Load initial user session
    // {|INICIO| 22/07/2026 às 16:05 - DEBUG: EVITAR FETCH DE PERFIL QUANDO HOUVER USUARIO LEGADO SEM TOKEN VALIDO}
    if (userData && tokenAutenticacaoCliente) {
      setCurrentUser(userData);
      fetchCompleteUserData(userData.id);
    } else if (userData && !tokenAutenticacaoCliente) {
      // Limpa sessão legada inconsistente sem toast de expiração.
      limparAutenticacao(false);
    } else if (!existeSessaoPersistida && estaNoFluxoDeConvite) {
      // No convite, acesso deslogado é esperado; não emitir alerta de sessão expirada.
      setCurrentUser(null);
    }
    // {|FIM| 22/07/2026 às 15:55 - CONVITE: EVITAR ALERTA FALSO DE SESSAO EXPIRADA AO ABRIR LINK DESLOGADO}
    // {|FIM| 22/07/2026 às 16:05 - DEBUG: EVITAR FETCH DE PERFIL QUANDO HOUVER USUARIO LEGADO SEM TOKEN VALIDO}

    const handleUserLogin = (event: Event) => {
      const customEvent = event as CustomEvent;
      if (customEvent.detail && customEvent.detail.user) {
        setCurrentUser(customEvent.detail.user);
        // {|INICIO| 22/07/2026 às 22:50 - ETAPA 7: ABRIR MODAL DE TROCA OBRIGATORIA LOGO APOS LOGIN DE CONTA LEGADA}
        setShowTrocarSenha(Boolean(customEvent.detail.user.exigir_troca_senha));
        // {|FIM| 22/07/2026 às 22:50 - ETAPA 7: ABRIR MODAL DE TROCA OBRIGATORIA LOGO APOS LOGIN DE CONTA LEGADA}
        fetchCompleteUserData(customEvent.detail.user.id);
      }
    };

    const handleUserLogout = (event: Event) => {
      const customEvent = event as CustomEvent;
      const isForced = customEvent.detail?.isForced ?? false;
      
      setCurrentUser(null);
      // Close all user-related modals/views
      setShowMyReservations(false);
      setShowMyRaces(false);
      setShowParticipantDashboard(false);
      setShowConsultarPontos(false);
      setShowTrocarSenha(false);
      setShowNovoCadastro(false);
      setShowMeuPerfil(false);
      
      // Show toast if it's a forced logout (session expired)
      if (isForced) {
        toastError({
          title: 'Erro',
          message: t('errors.sessionExpired'),
          position: 'center',
          class: 'izi-center-force error-style',
          timeout: 5000,
        });
      }
    };

    window.addEventListener('userLogin', handleUserLogin);
    window.addEventListener('userLogout', handleUserLogout);

    return () => {
      window.removeEventListener('userLogin', handleUserLogin);
      window.removeEventListener('userLogout', handleUserLogout);
    };
  }, [inviteToken, t]);

  // Monitorar mudança de dia para recarregar a aplicação (evitar data obsoleta)
  useEffect(() => {
    let lastCheckedDate = new Date().toDateString();

    const interval = setInterval(() => {
      const currentDate = new Date().toDateString();
      if (currentDate !== lastCheckedDate) {
        console.log('Detectada mudança de dia. Recarregando aplicação para atualizar calendário...');
        window.location.reload();
      }
    }, 60000); // Verifica a cada 1 minuto

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    // Carregar slots e atualizar calendário (alinhado ao legado)
    loadAvailableSlotsAndCalendar();
  }, [selectedDate]);

  // Close mobile menu when clicking outside or on window resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) { // md breakpoint
        setIsMobileMenuOpen(false);
      }
    };

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isMobileMenuOpen && !target.closest('.mobile-menu-container') && !target.closest('.mobile-menu-button')) {
        setIsMobileMenuOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    document.addEventListener('click', handleClickOutside);

    return () => {
      window.removeEventListener('resize', handleResize);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Overflow lock for PagarmeTestSuite modal
  useEffect(() => {
    if (showPagarmeTest) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [showPagarmeTest]);

  // Overflow lock for WebhookMonitoringDashboard modal
  useEffect(() => {
    if (showWebhookMonitoring) {
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [showWebhookMonitoring]);

  // {|INICIO| 11/06/2026 às 21:40 - AGENDA: SUAVIZAR REFRESH MANTENDO LISTA VISÍVEL DURANTE ATUALIZAÇÃO}
  // Comentário (function-level): Define o tipo de carregamento da agenda para diferenciar primeira carga de atualização silenciosa.
  const iniciarCarregamentoAgenda = () => {
    if (agendaJaCarregada) {
      setAtualizandoAgendaSilenciosamente(true);
      setCarregandoAgendaInicial(false);
      return;
    }
    setCarregandoAgendaInicial(true);
  };

  // Comentário (function-level): Finaliza qualquer estado de carregamento da agenda sem desmontar os cards já exibidos.
  const finalizarCarregamentoAgenda = () => {
    setAgendaJaCarregada(true);
    setCarregandoAgendaInicial(false);
    setAtualizandoAgendaSilenciosamente(false);
  };
  // {|FIM| 11/06/2026 às 21:40 - AGENDA: SUAVIZAR REFRESH MANTENDO LISTA VISÍVEL DURANTE ATUALIZAÇÃO}

  // Função combinada para atualizar slots e calendário
  const loadAvailableSlotsAndCalendar = async () => {
    if (!selectedDate) return;
    
    // {|INICIO| 11/06/2026 às 21:40 - AGENDA: SUAVIZAR REFRESH MANTENDO LISTA VISÍVEL DURANTE ATUALIZAÇÃO}
    iniciarCarregamentoAgenda();
    // {|FIM| 11/06/2026 às 21:40 - AGENDA: SUAVIZAR REFRESH MANTENDO LISTA VISÍVEL DURANTE ATUALIZAÇÃO}
    try {
      // Carrega slots disponíveis
      // {|INICIO| 21/06/2026 às 21:31 - AGENDA: USAR FORMATAÇÃO LOCAL ESTÁVEL DA DATA NAS CHAMADAS INICIAIS}
      // Versão anterior (histórico): usava `toISOString()` e dependia do fuso/horário presente no objeto Date.
      // const formattedDate = selectedDate.toISOString().split('T')[0];
      const formattedDate = formatarDataParaParametroUrl(selectedDate);
      // {|FIM| 21/06/2026 às 21:31 - AGENDA: USAR FORMATAÇÃO LOCAL ESTÁVEL DA DATA NAS CHAMADAS INICIAIS}
      const slots = await ReservationService.getAvailableSlots(formattedDate);
      setAvailableSlots(slots);
      
      // Atualiza dias indisponíveis do calendário
      await refreshCalendarDays(selectedDate);
      
    } catch (error) {
      console.error('Error loading slots and calendar:', error);
      
      // Improved error handling with user-friendly messages
      let errorMessage = 'Erro ao carregar horários disponíveis.';
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('ERR_ABORTED')) {
          errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.';
        } else if (error.message.includes('JSON')) {
          errorMessage = 'Erro de comunicação com o servidor. Tente novamente em alguns instantes.';
        } else {
          errorMessage = error.message;
        }
      }
      
      // Mostrar erro ao usuário usando util centralizado
      toastError({
        title: 'Erro',
        message: errorMessage,
        position: 'center',
        class: 'izi-center-force error-style',
        timeout: 5000,
      });
      
      // {|INICIO| 11/06/2026 às 21:40 - AGENDA: PRESERVAR LISTA ANTERIOR QUANDO FALHAR REFRESH SILENCIOSO}
      // Versão anterior (histórico): limpava a lista em qualquer erro e causava efeito visual de desmontagem da tela.
      // setAvailableSlots([]);
      if (availableSlots.length === 0) {
        setAvailableSlots([]);
      }
      // {|FIM| 11/06/2026 às 21:40 - AGENDA: PRESERVAR LISTA ANTERIOR QUANDO FALHAR REFRESH SILENCIOSO}
    } finally {
      // {|INICIO| 11/06/2026 às 21:40 - AGENDA: SUAVIZAR REFRESH MANTENDO LISTA VISÍVEL DURANTE ATUALIZAÇÃO}
      finalizarCarregamentoAgenda();
      // {|FIM| 11/06/2026 às 21:40 - AGENDA: SUAVIZAR REFRESH MANTENDO LISTA VISÍVEL DURANTE ATUALIZAÇÃO}
    }
  };

  // Comentário (function-level): Retorna a lista de campos obrigatórios que estão faltando
  // no cadastro do usuário para permitir uma reserva direta.
  const getMissingProfileFields = (user: User | null): string[] => {
    if (!user) return ['Usuário não logado'];

    // {|INICIO| 15/05/2026 às 11:05 - PERFIL: campos obrigatórios variam por país (endereço internacional sem CEP/Bairro obrigatório) }
    // Versão anterior (histórico): sempre exigia CEP/Bairro/UF/Cidade/Número, independente do país.
    // const requiredFields = [
    //   { key: 'nome', value: user.nome, labelKey: 'fields.name' },
    //   { key: 'cpf_cnpj', value: user.cpf_cnpj, labelKey: 'fields.cpf_cnpj' },
    //   { key: 'email', value: user.email, labelKey: 'fields.email' },
    //   { key: 'data_nascimento', value: user.data_nascimento, labelKey: 'fields.birthDate' },
    //   { key: 'telefone_celular', value: user.telefone_celular, labelKey: 'fields.cellPhone' },
    //   { key: 'endereco', value: user.endereco, labelKey: 'fields.address' },
    //   { key: 'numero', value: user.numero, labelKey: 'fields.number' },
    //   { key: 'bairro', value: user.bairro, labelKey: 'fields.neighborhood' },
    //   { key: 'cidade', value: user.cidade, labelKey: 'fields.city' },
    //   { key: 'estado', value: user.estado, labelKey: 'fields.state' },
    //   { key: 'cep', value: user.cep, labelKey: 'fields.zipCode' }
    // ];

    /**
     * Comentário (function-level): Para estrangeiro (não Brasil), não exigimos CEP nem Bairro para liberar a reserva/pagamento,
     * pois o endereço é digitado manualmente e pode não seguir padrão brasileiro.
     */
    const codigoIbgePais = Number((user as any)?.codigo_ibge_pais ?? 1058) || 1058;
    const isPaisBrasil = codigoIbgePais === 1058;

    const requiredFieldsBase = [
      { key: 'nome', value: user.nome, labelKey: 'fields.name' },
      { key: 'cpf_cnpj', value: user.cpf_cnpj, labelKey: 'fields.cpf_cnpj' },
      { key: 'email', value: user.email, labelKey: 'fields.email' },
      { key: 'data_nascimento', value: user.data_nascimento, labelKey: 'fields.birthDate' },
      { key: 'telefone_celular', value: user.telefone_celular, labelKey: 'fields.cellPhone' },
    ];

    const requiredAddressCommon = [
      { key: 'endereco', value: user.endereco, labelKey: 'fields.address' },
      { key: 'numero', value: user.numero, labelKey: 'fields.number' },
      { key: 'cidade', value: user.cidade, labelKey: 'fields.city' },
      { key: 'estado', value: user.estado, labelKey: 'fields.state' },
    ];

    const requiredAddressBrazilOnly = [
      { key: 'bairro', value: user.bairro, labelKey: 'fields.neighborhood' },
      { key: 'cep', value: user.cep, labelKey: 'fields.zipCode' },
    ];

    const requiredFields = [
      ...requiredFieldsBase,
      ...requiredAddressCommon,
      ...(isPaisBrasil ? requiredAddressBrazilOnly : []),
    ];
    // {|FIM| 15/05/2026 às 11:05 - PERFIL: campos obrigatórios variam por país (endereço internacional sem CEP/Bairro obrigatório) }

    return requiredFields
      .filter(field => {
        const text = (field.value ?? '').toString().trim();
        return text.length === 0;
      })
      .map(field => t(field.labelKey));
  };

  const loadAvailableSlots = async () => {
    if (!selectedDate) return;
    
    // {|INICIO| 11/06/2026 às 21:40 - AGENDA: SUAVIZAR REFRESH MANTENDO LISTA VISÍVEL DURANTE ATUALIZAÇÃO}
    iniciarCarregamentoAgenda();
    // {|FIM| 11/06/2026 às 21:40 - AGENDA: SUAVIZAR REFRESH MANTENDO LISTA VISÍVEL DURANTE ATUALIZAÇÃO}
    try {
      // Format date to YYYY-MM-DD format for API
      // {|INICIO| 21/06/2026 às 21:31 - AGENDA: PADRONIZAR DATA LOCAL TAMBÉM NO REFRESH MANUAL DOS HORÁRIOS}
      // Versão anterior (histórico): usava `toISOString()` e podia divergir da data lógica exibida no calendário.
      // const formattedDate = selectedDate.toISOString().split('T')[0];
      const formattedDate = formatarDataParaParametroUrl(selectedDate);
      // {|FIM| 21/06/2026 às 21:31 - AGENDA: PADRONIZAR DATA LOCAL TAMBÉM NO REFRESH MANUAL DOS HORÁRIOS}
      // {|INICIO| 12/06/2026 às 01:18 - AGENDA: REMOVER FALLBACK MOCK NO REFRESH MANUAL DOS HORARIOS}
      // Versão anterior (histórico): trocava resposta vazia ou sem valor por dados de teste, gerando horários falsos ao atualizar.
      // let slots = await ReservationService.getAvailableSlots(formattedDate);
      // if (!slots || slots.length === 0 || slots.every(slot => !slot.valorliquido || slot.valorliquido === 0)) {
      //   console.warn('API retornou slots sem valores, usando dados de teste');
      //   slots = [/* dados de teste removidos */];
      // }
      const slots = await ReservationService.getAvailableSlots(formattedDate);
      setAvailableSlots(Array.isArray(slots) ? slots : []);
      // {|FIM| 12/06/2026 às 01:18 - AGENDA: REMOVER FALLBACK MOCK NO REFRESH MANUAL DOS HORARIOS}
    } catch (error) {
      console.error('Error loading slots:', error);
      
      // Improved error handling with user-friendly messages
      let errorMessage = 'Erro ao carregar horários disponíveis.';
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('ERR_ABORTED')) {
          errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.';
        } else if (error.message.includes('JSON')) {
          errorMessage = 'Erro de comunicação com o servidor. Tente novamente em alguns instantes.';
        } else {
          errorMessage = error.message;
        }
      }
      
      // Mostrar erro ao usuário usando util centralizado
      toastError({
        title: 'Erro',
        message: errorMessage,
        position: 'center',
        class: 'izi-center-force error-style',
        timeout: 5000,
      });
      
      // {|INICIO| 12/06/2026 às 01:18 - AGENDA: PRESERVAR ESTADO REAL QUANDO O REFRESH MANUAL FALHAR}
      // Versão anterior (histórico): substituía falha por dados mock, mascarando a indisponibilidade real da data.
      if (availableSlots.length === 0) {
        setAvailableSlots([]);
      }
      // {|FIM| 12/06/2026 às 01:18 - AGENDA: PRESERVAR ESTADO REAL QUANDO O REFRESH MANUAL FALHAR}
    } finally {
      // {|INICIO| 11/06/2026 às 21:40 - AGENDA: SUAVIZAR REFRESH MANTENDO LISTA VISÍVEL DURANTE ATUALIZAÇÃO}
      finalizarCarregamentoAgenda();
      // {|FIM| 11/06/2026 às 21:40 - AGENDA: SUAVIZAR REFRESH MANTENDO LISTA VISÍVEL DURANTE ATUALIZAÇÃO}
    }
  };

  // Timer de atualização automática de dados (configurável, padrão 15 segundos)
  // Pausa automaticamente quando usuário está interagindo com a tela
  // Reinicia automaticamente quando a data selecionada muda
  // Agora atualiza tanto slots quanto dias indisponíveis do calendário
  // {|INICIO| 02/04/2026 às 10:28 - LINT: REMOVER DESESTRUTURAÇÃO VAZIA}
  useDataRefreshTimer(
    loadAvailableSlotsAndCalendar,
    {
      refreshIntervalMs: 15 * 1000, // 15 segundos
      enabled: true, // Habilitado por padrão
      pauseOnUserInteraction: true, // Pausar durante interação
      userIdleTimeoutMs: 5000, // 5 segundos para considerar usuário inativo (aumentado)
      dependencies: [selectedDate] // Reinicia quando data muda
    }
  );
  // {|FIM| 02/04/2026 às 10:28 - LINT: REMOVER DESESTRUTURAÇÃO VAZIA}

  const handleLogin = async (email: string, password: string) => {
    try {
      const user = await AuthService.login(email, password);
      setCurrentUser(user);
      setShowAuthModal(false);
      // {|INICIO| 22/07/2026 às 22:50 - ETAPA 7: BLOQUEAR CONTINUIDADE ATE A TROCA DE SENHA OBRIGATORIA}
      setShowTrocarSenha(Boolean(user.exigir_troca_senha));
      // {|FIM| 22/07/2026 às 22:50 - ETAPA 7: BLOQUEAR CONTINUIDADE ATE A TROCA DE SENHA OBRIGATORIA}
    } catch (error) {
      console.error('Login error:', error);
      
      // Improved error handling for login
      let errorMessage = 'Erro ao fazer login.';
      
      if (error instanceof Error) {
        if (error.message.includes('Failed to fetch') || error.message.includes('ERR_ABORTED')) {
          errorMessage = 'Não foi possível conectar ao servidor. Verifique sua conexão com a internet.';
        } else if (error.message.includes('JSON')) {
          errorMessage = 'Erro de comunicação com o servidor. Tente novamente em alguns instantes.';
        } else {
          errorMessage = error.message;
        }
      }
      
      throw new Error(errorMessage);
    }
  };

  const handleLogout = async () => {
    await AuthService.logout();
    setCurrentUser(null);
  };

  // Comentário (function-level): Centraliza o fluxo ao selecionar um horário
  // para reserva. Garante login e checa se o cadastro está completo antes de
  // abrir o modal de reserva, redirecionando para "Meu Perfil" quando necessário.
  const handleReservationSelect = (agenda: Agenda) => {
    if (!currentUser) {
      setShowAuthModal(true);
      return;
    }

    const missingFields = getMissingProfileFields(currentUser);
    if (missingFields.length > 0) {
      toastError({
        title: t('app.profileIncompleteTitle'),
        message: `${t('app.profileIncompleteMessage')} ${missingFields.join(', ')}`,
        position: 'center',
        class: 'izi-center-force error-style',
        timeout: 8000,
      });
      setShowMeuPerfil(true);
      return;
    }

    setSelectedAgenda(agenda);
    setShowReservationModal(true);
  };

  // {|INICIO| 11/06/2026 às 21:05 - COMPARTILHAMENTO: CONSUMIR LINK DA DATA E LIMPAR QUERY STRING APÓS INTERAÇÃO}
  // Comentário (function-level): Atualiza a data selecionada e remove o parâmetro compartilhado após a primeira interação/navegação do calendário.
  const handleDateChange = (dataSelecionada: Date) => {
    if (referenciaJaConsumiuDataCompartilhada.current) {
      removerParametroDataCompartilhadaDaUrl();
      referenciaJaConsumiuDataCompartilhada.current = false;
    }

    // {|INICIO| 21/06/2026 às 21:31 - DATA INICIAL: NORMALIZAR DATA SELECIONADA EM TODAS AS ORIGENS}
    setSelectedDate(normalizarDataSemHorario(dataSelecionada));
    // {|FIM| 21/06/2026 às 21:31 - DATA INICIAL: NORMALIZAR DATA SELECIONADA EM TODAS AS ORIGENS}
  };
  // {|FIM| 11/06/2026 às 21:05 - COMPARTILHAMENTO: CONSUMIR LINK DA DATA E LIMPAR QUERY STRING APÓS INTERAÇÃO}

  // {|INICIO| 11/06/2026 às 20:47 - COMPARTILHAMENTO: ADICIONAR AÇÃO NATIVA COM FALLBACK PARA CÓPIA DO LINK}
  // Comentário (function-level): Compartilha o link da data selecionada com Web Share API e, se necessário, copia o link para a área de transferência.
  const compartilharDataSelecionada = async () => {
    const linkCompartilhamento = montarLinkCompartilhamentoData(selectedDate);
    const mensagemCompartilhamento = t('app.shareDateMessage', {
      date: selectedDate.toLocaleDateString('pt-BR'),
    });

    try {
      if (navigator.share) {
        await navigator.share({
          title: t('app.availableSlots'),
          text: mensagemCompartilhamento,
          url: linkCompartilhamento,
        });
        return;
      }

      const copiouLink = await copiarTextoParaAreaTransferencia(linkCompartilhamento);
      if (copiouLink) {
        await toastSuccess({
          title: t('common.success'),
          message: t('app.shareDateCopied'),
          position: 'center',
          class: 'izi-center-force success-style',
          timeout: 2500,
        });
        return;
      }
    } catch (erroCompartilhamento) {
      if (erroCompartilhamento instanceof DOMException && erroCompartilhamento.name === 'AbortError') {
        return;
      }

      const copiouLink = await copiarTextoParaAreaTransferencia(linkCompartilhamento);
      if (copiouLink) {
        await toastSuccess({
          title: t('common.success'),
          message: t('app.shareDateCopied'),
          position: 'center',
          class: 'izi-center-force success-style',
          timeout: 2500,
        });
        return;
      }
    }

    await toastError({
      title: t('common.error'),
      message: t('app.shareDateCopyError'),
      position: 'center',
      class: 'izi-center-force error-style',
      timeout: 4000,
    });
  };
  // {|FIM| 11/06/2026 às 20:47 - COMPARTILHAMENTO: ADICIONAR AÇÃO NATIVA COM FALLBACK PARA CÓPIA DO LINK}

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  // {|INICIO| 22/07/2026 às 22:50 - ETAPA 7: ATIVAR TELA DE RESET POR TOKEN DENTRO DA SPA PRINCIPAL}
  if (resetPasswordToken && !inviteToken) {
    return (
      <Suspense fallback={<GlobalSpinner show={true} />}>
        <ResetPasswordPage />
      </Suspense>
    );
  }
  // {|FIM| 22/07/2026 às 22:50 - ETAPA 7: ATIVAR TELA DE RESET POR TOKEN DENTRO DA SPA PRINCIPAL}

  // If there's an invite token, show invite acceptance page
  if (inviteToken) {
    return (
      <>
        <EnhancedInviteAcceptance token={inviteToken} onLogin={setCurrentUser} />
        {showTrocarSenha && currentUser && (
          <Suspense fallback={<GlobalSpinner show={true} />}>
            <TrocarSenhaModal
              currentUser={currentUser}
              trocaObrigatoria={Boolean(currentUser.exigir_troca_senha)}
              onClose={() => setShowTrocarSenha(false)}
              onLogout={() => {
                setCurrentUser(null);
                setShowTrocarSenha(false);
              }}
            />
          </Suspense>
        )}
      </>
    );
  }

  // {|INICIO| 12/03/2026 às 18:58 - SCROLL DESKTOP: TRAVAR BODY E LIMITAR SCROLL AO QUADRO DE HORÁRIOS}
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-primary transition-colors duration-200 lg:h-screen lg:overflow-hidden lg:flex lg:flex-col">
          {/* API Status Check */}
          <ApiStatusCheck apiBaseUrl={getApiBaseUrl()} />
          
          {/* Header */}
          <div className="sticky top-0 left-0 right-0 z-50 flex flex-col w-full flex-shrink-0">
          <nav className="bg-white dark:bg-dark-secondary shadow-lg border-b-4 border-red-600 transition-colors duration-200 mobile-menu-container w-full">
            {/* {|INICIO| 12/03/2026 às 19:25 - HEADER: DISTRIBUIR LARGURA E EVITAR TÍTULO GRUDADO NO SELETOR DE IDIOMA} */}
            {/* {|INICIO| 12/03/2026 às 19:32 - LAYOUT: RESTAURAR MARGENS LATERAIS (DIREITA/ESQUERDA) CONFORME REFERÊNCIA} */}
            {/* {|INICIO| 12/03/2026 às 19:37 - LAYOUT: REDUZIR MARGENS LATERAIS NO DESKTOP PARA DISTRIBUIR MELHOR O CONTEÚDO} */}
            {/* {|INICIO| 12/03/2026 às 19:41 - LAYOUT: REDUZIR AINDA MAIS MARGENS LATERAIS (REF. QUADRO VERMELHO)} */}
            {/* {|INICIO| 12/03/2026 às 20:38 - HEADER: ALINHAR PADDING LATERAL COM BANNER/QUADROS (MESMA MARGEM NO 100%)} */}
            {/* {|INICIO| 12/03/2026 às 20:45 - LAYOUT: REMOVER LIMITE 2XL PARA REDUZIR MARGENS EM TELAS MUITO LARGAS} */}
            <div className="max-w-7xl xl:max-w-[96rem] 2xl:max-w-none mx-auto px-3 sm:px-6 lg:px-8">
            {/* {|FIM| 12/03/2026 às 20:45 - LAYOUT: REMOVER LIMITE 2XL PARA REDUZIR MARGENS EM TELAS MUITO LARGAS} */}
            {/* {|FIM| 12/03/2026 às 20:38 - HEADER: ALINHAR PADDING LATERAL COM BANNER/QUADROS (MESMA MARGEM NO 100%)} */}
            {/* {|FIM| 12/03/2026 às 19:41 - LAYOUT: REDUZIR AINDA MAIS MARGENS LATERAIS (REF. QUADRO VERMELHO)} */}
              <div className="flex justify-between items-center min-h-16 py-2 gap-3 min-w-0">
                {/* {|INICIO| 22/06/2026 às 14:44 - HEADER: DAR MAIS DESTAQUE VISUAL PARA A LOGO COM CONTAINER DISCRETO E AJUSTE LEVE DE TEXTOS} */}
                {/* {|INICIO| 22/06/2026 às 14:47 - HEADER: REFORÇAR BLOCO DA LOGO, AUMENTAR LEVEMENTE A MARCA E SUAVIZAR MAIS O SUBTÍTULO} */}
                {/* {|INICIO| 22/06/2026 às 14:50 - HEADER: ADAPTAR CONTAINER PARA A PROPORÇÃO HORIZONTAL DA LOGO E AMPLIAR PRESENÇA VISUAL} */}
                <div className="flex items-center gap-4 min-w-0 flex-1">
                  {/* {|INICIO| 24/06/2026 às 14:50 - HEADER: ESCURECER FUNDO DA LOGO NO TEMA CLARO NO PROJETO PUBLICADO} */}
                  <div
                    className="flex h-12 min-w-[4.75rem] px-2.5 sm:h-14 sm:min-w-[5.75rem] sm:px-3.5 lg:h-16 lg:min-w-[6.75rem] lg:px-4 flex-shrink-0 items-center justify-center rounded-2xl border shadow-lg ring-1"
                    style={{
                      ...estiloContainerLogo,
                      boxShadow: '0 10px 18px rgba(15, 23, 42, 0.18)',
                    }}
                  >
                    {/* {|INICIO| 14/07/2026 às 00:28 - USAR BASE DA APLICACAO PARA A LOGO DO HEADER NO FRONT PUBLICADO} */}
                    <img 
                      src={`${obterBaseAplicacaoAtual()}img/favicon.png`} 
                      alt="KGV Logo" 
                      className="h-8 w-auto sm:h-10 lg:h-12 object-contain drop-shadow-[0_2px_4px_rgba(0,0,0,0.24)]"
                    />
                    {/* {|FIM| 14/07/2026 às 00:28 - USAR BASE DA APLICACAO PARA A LOGO DO HEADER NO FRONT PUBLICADO} */}
                  </div>
                  {/* {|FIM| 24/06/2026 às 14:50 - HEADER: ESCURECER FUNDO DA LOGO NO TEMA CLARO NO PROJETO PUBLICADO} */}
                  <div className="min-w-0 flex-1">
                    <h1 className="text-sm sm:text-lg lg:text-[1.35rem] font-extrabold tracking-tight text-gray-900 dark:text-dark-primary whitespace-nowrap overflow-hidden text-ellipsis">{t('app.title')}</h1>
                    <p className="text-[10px] sm:text-[11px] lg:text-xs text-red-600/75 font-medium tracking-[0.02em] whitespace-nowrap overflow-hidden text-ellipsis">{t('app.subtitle')}</p>
                  </div>
                </div>
                {/* {|FIM| 22/06/2026 às 14:50 - HEADER: ADAPTAR CONTAINER PARA A PROPORÇÃO HORIZONTAL DA LOGO E AMPLIAR PRESENÇA VISUAL} */}
                {/* {|FIM| 22/06/2026 às 14:47 - HEADER: REFORÇAR BLOCO DA LOGO, AUMENTAR LEVEMENTE A MARCA E SUAVIZAR MAIS O SUBTÍTULO} */}
                {/* {|FIM| 22/06/2026 às 14:44 - HEADER: DAR MAIS DESTAQUE VISUAL PARA A LOGO COM CONTAINER DISCRETO E AJUSTE LEVE DE TEXTOS} */}

                <div className="flex items-center justify-end flex-shrink-0">
                  {/* Language and Theme Controls - Always visible */}
                  <div className="flex items-center gap-2 mr-2">
                    <LanguageSelector showLabel={false} className="min-w-[48px]" />
                    <ThemeToggle showLabel={false} />
                  </div>
                  
                  {/* Mobile Menu Button - Only visible on mobile */}
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsMobileMenuOpen(!isMobileMenuOpen);
                    }}
                    className={`${mobileMenuClass} flex items-center justify-center w-10 h-10 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-md mobile-menu-button`}
                  >
                    {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
                  </button>
                  
                  {/* Desktop buttons - Hidden on mobile */}
                  <div className={`${desktopMenuClass} items-center gap-1 sm:gap-3 overflow-x-auto`}>
                    {(() => {
                      /**
                       * buildMenuItemsDesktop: constrói as ações do menu para desktop,
                       * reduz duplicação entre estados logado/não logado e mantém
                       * consistência com o menu mobile.
                       */
                      const items: Array<{
                        key: string;
                        type: 'link' | 'button';
                        label: string;
                        icon: React.ReactElement;
                        className: string;
                        onClick?: () => void;
                        href?: string;
                      }> = [];

                      // Home (comum)
                      items.push({
                        key: 'home',
                        type: 'link',
                        label: t('navigation.home'),
                        icon: <Home className="w-4 h-4" />,
                        className: 'flex items-center space-x-2 px-2 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-md h-10 flex-shrink-0',
                        href: 'https://kartodromogranjaviana.com.br/',
                      });

                      if (currentUser) {
                        items.push(
                          {
                            key: 'points',
                            type: 'button',
                            label: t('app.consultPoints'),
                            icon: <Coins className="w-4 h-4" />,
                            className: 'flex items-center space-x-2 px-2 sm:px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors shadow-md h-10 flex-shrink-0',
                            onClick: () => setShowConsultarPontos(true),
                          },
                          {
                            key: 'profile',
                            type: 'button',
                            label: t('app.myProfile'),
                            icon: <UserIcon className="w-4 h-4" />,
                            className: 'flex items-center space-x-2 px-2 sm:px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-md h-10 flex-shrink-0',
                            onClick: () => setShowMeuPerfil(true),
                          },
                          {
                            key: 'password',
                            type: 'button',
                            label: t('app.changePassword'),
                            icon: <Key className="w-4 h-4" />,
                            className: 'flex items-center space-x-2 px-2 sm:px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-md h-10 flex-shrink-0',
                            onClick: () => setShowTrocarSenha(true),
                          },
                          {
                          key: 'reservations',
                          type: 'button',
                          label: t('app.myReservations'),
                          icon: <Calendar className="w-4 h-4" />,
                          className: 'flex items-center space-x-2 px-2 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md h-10 flex-shrink-0',
                          onClick: () => setShowMyReservations(true),
                        },
                        {
                          key: 'races',
                          type: 'button',
                          // {|INICIO| 06/05/2026 às 16:05 - I18N: TRADUZIR BOTÃO "MINHAS CORRIDAS"}
                          label: t('myRaces.title'),
                          // {|FIM| 06/05/2026 às 16:05 - I18N: TRADUZIR BOTÃO "MINHAS CORRIDAS"}
                          icon: <Flag className="w-4 h-4" />,
                          className: 'flex items-center space-x-2 px-2 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md h-10 flex-shrink-0',
                          onClick: () => setShowMyRaces(true),
                        },
                        );
                        if (import.meta.env.VITE_ENABLE_PAGARME_TEST_BUTTON === 'true') {
                          items.push({
                            key: 'pagarme-test',
                            type: 'button',
                            label: 'Teste Pagar.me',
                            icon: <CreditCard className="w-4 h-4" />,
                            className: 'flex items-center space-x-2 px-2 sm:px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors shadow-md h-10 flex-shrink-0',
                            onClick: () => setShowPagarmeTest(true),
                          });
                        }
                        items.push({
                          key: 'logout',
                          type: 'button',
                          label: t('auth.logout'),
                          icon: <LogOut className="w-4 h-4" />,
                          className: 'flex items-center space-x-2 px-2 sm:px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md h-10 flex-shrink-0',
                          onClick: () => void handleLogout(),
                        });
                      } else {
                        items.push(
                          {
                            key: 'register',
                            type: 'button',
                            label: t('app.newRegistration'),
                            icon: <UserPlus className="w-4 h-4" />,
                            className: 'flex items-center space-x-2 px-2 sm:px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md h-10 flex-shrink-0',
                            onClick: () => setShowNovoCadastro(true),
                          },
                          {
                            key: 'login',
                            type: 'button',
                            label: t('auth.login'),
                            icon: <UserIcon className="w-4 h-4" />,
                            className: 'flex items-center space-x-2 px-2 sm:px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md h-10 flex-shrink-0',
                            onClick: () => setShowAuthModal(true),
                          },
                        );
                      }

                      return (
                        <>
                          {items.map((item) =>
                            item.type === 'link' ? (
                              <a
                                key={item.key}
                                href={item.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={item.className}
                              >
                                {item.icon}
                                <span className="hidden sm:inline">{item.label}</span>
                              </a>
                            ) : (
                              <button
                                key={item.key}
                                onClick={item.onClick}
                                className={item.className}
                              >
                                {item.icon}
                                <span className="hidden sm:inline">{item.label}</span>
                              </button>
                            )
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>
              </div>
              
              {/* Mobile Menu Dropdown */}
              {isMobileMenuOpen && (
                <div className={`${shouldUseDropdown ? 'block' : 'hidden'} bg-white dark:bg-dark-secondary border-t border-gray-200 dark:border-gray-700 shadow-lg w-full`}>
                  <div className="dropdown-panel px-4 py-3">
                    <div className="dropdown-menu">
                      {(() => {
                        /**
                         * buildMenuItemsMobile: constrói as ações do menu para mobile,
                         * fecha o dropdown ao clicar e evita duplicação com desktop.
                         */
                        const items: Array<{
                          key: string;
                          type: 'link' | 'button';
                          label: string;
                          icon: React.ReactElement;
                          className: string;
                          onClick?: () => Promise<void> | void;
                          href?: string;
                        }> = [];

                        // Home (comum)
                        items.push({
                          key: 'home',
                          type: 'link',
                          label: t('navigation.home'),
                          icon: <Home className="w-5 h-5" />,
                          className: 'flex items-center space-x-3 px-4 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors shadow-md w-full',
                          href: 'https://kartodromogranjaviana.com.br/',
                          onClick: () => setIsMobileMenuOpen(false),
                        });

                        if (currentUser) {
                          items.push(
                            {
                              key: 'points',
                              type: 'button',
                              label: t('app.consultPoints'),
                              icon: <Coins className="w-5 h-5" />,
                              className: 'flex items-center space-x-3 px-4 py-3 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors shadow-md w-full',
                              onClick: () => { setShowConsultarPontos(true); setIsMobileMenuOpen(false); },
                            },
                            {
                              key: 'profile',
                              type: 'button',
                              label: t('app.myProfile'),
                              icon: <UserIcon className="w-5 h-5" />,
                              className: 'flex items-center space-x-3 px-4 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-md w-full',
                              onClick: () => { setShowMeuPerfil(true); setIsMobileMenuOpen(false); },
                            },
                            {
                              key: 'password',
                              type: 'button',
                              label: t('app.changePassword'),
                              icon: <Key className="w-5 h-5" />,
                              className: 'flex items-center space-x-3 px-4 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors shadow-md w-full',
                              onClick: () => { setShowTrocarSenha(true); setIsMobileMenuOpen(false); },
                            },
                            {
                              key: 'reservations',
                              type: 'button',
                              label: t('app.myReservations'),
                              icon: <Calendar className="w-5 h-5" />,
                              className: 'flex items-center space-x-3 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md w-full',
                              onClick: () => { setShowMyReservations(true); setIsMobileMenuOpen(false); },
                            },
                            {
                              key: 'races',
                              type: 'button',
                              // {|INICIO| 06/05/2026 às 16:05 - I18N: TRADUZIR BOTÃO "MINHAS CORRIDAS" (MOBILE)}
                              label: t('myRaces.title'),
                              // {|FIM| 06/05/2026 às 16:05 - I18N: TRADUZIR BOTÃO "MINHAS CORRIDAS" (MOBILE)}
                              icon: <Flag className="w-5 h-5" />,
                              className: 'flex items-center space-x-3 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md w-full',
                              onClick: () => { setShowMyRaces(true); setIsMobileMenuOpen(false); },
                            },
                          );
                          if (import.meta.env.VITE_ENABLE_PAGARME_TEST_BUTTON === 'true') {
                            items.push({
                              key: 'pagarme-test',
                              type: 'button',
                              label: 'Teste Pagar.me',
                              icon: <CreditCard className="w-5 h-5" />,
                              className: 'flex items-center space-x-3 px-4 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors shadow-md w-full',
                              onClick: () => { setShowPagarmeTest(true); setIsMobileMenuOpen(false); },
                            });
                          }
                          items.push({
                            key: 'logout',
                            type: 'button',
                            label: t('auth.logout'),
                            icon: <LogOut className="w-5 h-5" />,
                            className: 'flex items-center space-x-3 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-md w-full',
                            onClick: async () => { await handleLogout(); setIsMobileMenuOpen(false); },
                          });
                        } else {
                          items.push(
                            {
                              key: 'register',
                              type: 'button',
                              label: t('app.newRegistration'),
                              icon: <UserPlus className="w-5 h-5" />,
                              className: 'flex items-center space-x-3 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-md w-full',
                              onClick: () => { setShowNovoCadastro(true); setIsMobileMenuOpen(false); },
                            },
                            {
                              key: 'login',
                              type: 'button',
                              label: t('auth.login'),
                              icon: <UserIcon className="w-5 h-5" />,
                              className: 'flex items-center space-x-3 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-md w-full',
                              onClick: () => { setShowAuthModal(true); setIsMobileMenuOpen(false); },
                            },
                          );
                        }

                        return (
                          <>
                            {items.map((item) =>
                              item.type === 'link' ? (
                                <a
                                  key={item.key}
                                  href={item.href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={item.onClick as any}
                                  className={item.className}
                                >
                                  {item.icon}
                                  <span>{item.label}</span>
                                </a>
                              ) : (
                                <button
                                  key={item.key}
                                  onClick={item.onClick}
                                  className={item.className}
                                >
                                  {item.icon}
                                  <span>{item.label}</span>
                                </button>
                              )
                            )}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}
            </div>
            {/* {|FIM| 12/03/2026 às 19:37 - LAYOUT: REDUZIR MARGENS LATERAIS NO DESKTOP PARA DISTRIBUIR MELHOR O CONTEÚDO} */}
            {/* {|FIM| 12/03/2026 às 19:32 - LAYOUT: RESTAURAR MARGENS LATERAIS (DIREITA/ESQUERDA) CONFORME REFERÊNCIA} */}
            {/* {|FIM| 12/03/2026 às 19:25 - HEADER: DISTRIBUIR LARGURA E EVITAR TÍTULO GRUDADO NO SELETOR DE IDIOMA} */}
      </nav>

        {/* Banner Informativo - Avisos Importantes */}
        {/* {|INICIO| 27/02/2026 às 10:15 - AJUSTE DE BANNER: BORDAS ARREDONDADAS E ALINHAMENTO COM CONTEÚDO} */}
        {/* {|INICIO| 12/03/2026 às 19:25 - LAYOUT: AUMENTAR LARGURA ÚTIL NO DESKTOP PARA REDUZIR MARGENS LATERAIS} */}
        {/* {|INICIO| 12/03/2026 às 19:32 - LAYOUT: RESTAURAR MARGENS LATERAIS (DIREITA/ESQUERDA) CONFORME REFERÊNCIA} */}
        {/* {|INICIO| 12/03/2026 às 19:37 - LAYOUT: REDUZIR MARGENS LATERAIS NO DESKTOP PARA DISTRIBUIR MELHOR O CONTEÚDO} */}
        {/* {|INICIO| 12/03/2026 às 19:41 - LAYOUT: REDUZIR AINDA MAIS MARGENS LATERAIS (REF. QUADRO VERMELHO)} */}
        {/* {|INICIO| 12/03/2026 às 20:45 - LAYOUT: REMOVER LIMITE 2XL PARA REDUZIR MARGENS EM TELAS MUITO LARGAS} */}
        <div className="max-w-7xl xl:max-w-[96rem] 2xl:max-w-none mx-auto px-3 sm:px-6 lg:px-8 mt-4">
          <div className="text-white px-4 py-[10px] shadow-lg rounded-lg flex items-center justify-center w-full border border-red-700" style={{ backgroundColor: '#dc3545' }}>
            {/* {|INICIO| 12/03/2026 às 20:28 - BANNER: CENTRALIZAR TEXTO NO 100% E ALINHAR COM QUADROS ABAIXO} */}
            <div className="flex flex-col items-center text-center space-y-1">
              {/* {|INICIO| 27/04/2026 às 00:25 - BANNER: EXIBIR AVISO VERMELHO VIA API (SUBSTITUIR TEXTOS FIXOS)} */}
              <div className="w-full grid grid-cols-[auto,1fr] items-stretch gap-3">
                <div className="flex items-center justify-center">
                  {/* {|INICIO| 27/04/2026 às 00:55 - BANNER: AUMENTAR TAMANHO DO TRIÂNGULO PRINCIPAL PARA MAIS DESTAQUE} */}
                  {/* {|INICIO| 27/04/2026 às 00:58 - BANNER: DOBRAR TAMANHO DO TRIÂNGULO PRINCIPAL} */}
                  <AlertTriangle className="flex-shrink-0 animate-pulse h-full max-h-48 sm:max-h-56 w-16 sm:w-20" />
                  {/* {|FIM| 27/04/2026 às 00:58 - BANNER: DOBRAR TAMANHO DO TRIÂNGULO PRINCIPAL} */}
                  {/* {|FIM| 27/04/2026 às 00:55 - BANNER: AUMENTAR TAMANHO DO TRIÂNGULO PRINCIPAL PARA MAIS DESTAQUE} */}
                </div>

                <div className="flex flex-col items-center text-center space-y-1 w-full">
                    {(htmlAvisoReservaOnlineRenderizado || '').trim() ? (
                    <div
                      className="font-bold text-center uppercase text-[10px] sm:text-sm leading-tight w-full"
                        dangerouslySetInnerHTML={{ __html: htmlAvisoReservaOnlineRenderizado }}
                    />
                  ) : (
                    <>
                      <div className="font-bold text-center uppercase text-[10px] sm:text-sm leading-tight w-full">
                        {t('app.pointsDiscontinuationNotice')}
                      </div>
                      <div className="font-semibold text-center uppercase text-[10px] sm:text-sm leading-tight w-full">
                        {t('app.pilotReservationWarning')}
                      </div>
                    </>
                  )}
                </div>
              </div>
              {/* {|FIM| 27/04/2026 às 00:25 - BANNER: EXIBIR AVISO VERMELHO VIA API (SUBSTITUIR TEXTOS FIXOS)} */}
            </div>
            {/* {|FIM| 12/03/2026 às 20:28 - BANNER: CENTRALIZAR TEXTO NO 100% E ALINHAR COM QUADROS ABAIXO} */}
          </div>
        </div>
        {/* {|FIM| 12/03/2026 às 20:45 - LAYOUT: REMOVER LIMITE 2XL PARA REDUZIR MARGENS EM TELAS MUITO LARGAS} */}
        {/* {|FIM| 12/03/2026 às 19:41 - LAYOUT: REDUZIR AINDA MAIS MARGENS LATERAIS (REF. QUADRO VERMELHO)} */}
        {/* {|FIM| 12/03/2026 às 19:37 - LAYOUT: REDUZIR MARGENS LATERAIS NO DESKTOP PARA DISTRIBUIR MELHOR O CONTEÚDO} */}
        {/* {|FIM| 12/03/2026 às 19:32 - LAYOUT: RESTAURAR MARGENS LATERAIS (DIREITA/ESQUERDA) CONFORME REFERÊNCIA} */}
        {/* {|FIM| 12/03/2026 às 19:25 - LAYOUT: AUMENTAR LARGURA ÚTIL NO DESKTOP PARA REDUZIR MARGENS LATERAIS} */}
        {/* {|FIM| 27/02/2026 às 10:15 - AJUSTE DE BANNER: BORDAS ARREDONDADAS E ALINHAMENTO COM CONTEÚDO} */}
      </div>

      {/* Main Content */}
      {/* {|INICIO| 12/03/2026 às 18:45 - SCROLL DESKTOP: LIMITAR SCROLL À LISTA DE HORÁRIOS} */}
      {/* {|INICIO| 12/03/2026 às 19:18 - AJUSTE VISUAL: REDUZIR MARGENS/PADDINGS NAS CAIXAS (CALENDÁRIO E LISTA)} */}
      {/* {|INICIO| 12/03/2026 às 19:25 - LAYOUT: AUMENTAR LARGURA ÚTIL NO DESKTOP PARA REDUZIR MARGENS LATERAIS} */}
      {/* {|INICIO| 12/03/2026 às 19:32 - LAYOUT: RESTAURAR MARGENS LATERAIS (DIREITA/ESQUERDA) CONFORME REFERÊNCIA} */}
      {/* {|INICIO| 12/03/2026 às 19:37 - LAYOUT: REDUZIR MARGENS LATERAIS NO DESKTOP PARA DISTRIBUIR MELHOR O CONTEÚDO} */}
      {/* {|INICIO| 12/03/2026 às 19:41 - LAYOUT: REDUZIR AINDA MAIS MARGENS LATERAIS (REF. QUADRO VERMELHO)} */}
      {/* {|INICIO| 12/03/2026 às 20:45 - LAYOUT: REMOVER LIMITE 2XL PARA REDUZIR MARGENS EM TELAS MUITO LARGAS} */}
      <main className="max-w-7xl xl:max-w-[96rem] 2xl:max-w-none mx-auto px-3 sm:px-6 lg:px-8 py-3 sm:py-6 lg:flex-1 lg:min-h-0 lg:overflow-hidden">

        {/* {|INICIO| 12/03/2026 às 20:08 - CALENDÁRIO: AMPLIAR ALTURA PARA OCUPAR ESPAÇO VAZIO (QUADRO VERMELHO)} */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6 lg:items-stretch lg:h-full lg:min-h-0">
          {/* Calendar Section */}
          <div className="lg:col-span-1 lg:h-full lg:min-h-0 lg:flex lg:flex-col">
            {/* {|INICIO| 12/03/2026 às 20:38 - CALENDÁRIO: REMOVER CARD DUPLO PARA COMPONENTE OCUPAR A ALTURA TOTAL} */}
            <div className="lg:flex-1 lg:min-h-0 lg:flex lg:flex-col">
              <CalendarView
                selectedDate={selectedDate}
                onDateChange={handleDateChange}
                disabledDates={[]}
                onRefreshCalendar={async () => {
                  // Quando o calendário atualiza (ex.: mudança de mês), recarregar slots
                  await loadAvailableSlots();
                }}
              />
            </div>
            {/* {|FIM| 12/03/2026 às 20:38 - CALENDÁRIO: REMOVER CARD DUPLO PARA COMPONENTE OCUPAR A ALTURA TOTAL} */}
          </div>

          {/* Available Slots */}
          <div className="lg:col-span-2 lg:h-full lg:min-h-0">
            <div className={`rounded-lg shadow-lg p-3 sm:p-5 lg:p-4 border lg:h-full lg:min-h-0 lg:flex lg:flex-col ${
              theme === 'dark' 
                ? 'bg-dark-surface border-dark-border' 
                : 'bg-white border-gray-200'
            }`}>
              {/* {|INICIO| 12/03/2026 às 19:48 - LISTA HORÁRIOS: COMPACTAR LINHAS NO DESKTOP PARA EXIBIR MAIS ITENS} */}
              {/* {|INICIO| 12/03/2026 às 21:05 - UI: ADICIONAR TOGGLE "OCULTAR INDISPONÍVEIS?" NO CABEÇALHO} */}
              {/* {|INICIO| 11/06/2026 às 20:47 - COMPARTILHAMENTO: INSERIR BOTÃO PARA COMPARTILHAR LINK DA DATA ATUAL} */}
              <div className="mb-2 sm:mb-3 lg:mb-2 flex flex-col gap-2 xl:flex-row xl:items-center xl:justify-between">
                <h2 className={`text-lg font-bold flex items-center space-x-2 ${
                  theme === 'dark' ? 'text-dark-primary' : 'text-gray-900'
                }`}>
                  <Clock className="w-5 h-5 text-blue-600" />
                  <span>{t('app.availableSlots')} - {selectedDate.toLocaleDateString('pt-BR')}</span>
                  {/* {|INICIO| 11/06/2026 às 21:40 - AGENDA: EXIBIR INDICADOR DISCRETO DURANTE ATUALIZAÇÃO SILENCIOSA} */}
                  {atualizandoAgendaSilenciosamente && (
                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${
                      theme === 'dark' ? 'text-dark-secondary' : 'text-gray-600'
                    }`}>
                      <span className="h-2 w-2 rounded-full bg-red-500 animate-pulse" />
                      <span>{t('common.loading')}...</span>
                    </span>
                  )}
                  {/* {|FIM| 11/06/2026 às 21:40 - AGENDA: EXIBIR INDICADOR DISCRETO DURANTE ATUALIZAÇÃO SILENCIOSA} */}
                </h2>

                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-end">
                  <button
                    type="button"
                    onClick={() => void compartilharDataSelecionada()}
                    className={`inline-flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs sm:text-sm font-medium transition-colors ${
                      theme === 'dark'
                        ? 'border border-dark-border bg-dark-hover text-dark-primary hover:border-red-500 hover:text-red-300'
                        : 'border border-gray-200 bg-gray-50 text-gray-700 hover:border-red-300 hover:text-red-600'
                    }`}
                    aria-label={t('app.shareDate')}
                  >
                    <Share2 className="w-4 h-4" />
                    <span>{t('app.shareDate')}</span>
                  </button>

                  <label className={`flex items-center justify-between sm:justify-end gap-3 select-none ${
                    theme === 'dark' ? 'text-dark-secondary' : 'text-gray-700'
                  }`}>
                    <span className="text-xs sm:text-sm font-medium">
                      {t('app.hideUnavailableSlots')}
                    </span>
                    <span className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={hideUnavailableSlots}
                        onChange={(e) => setHideUnavailableSlots(e.target.checked)}
                        className="sr-only peer"
                        aria-label={t('app.hideUnavailableSlots')}
                      />
                      <span className="w-10 h-5 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-red-300 dark:peer-focus:ring-red-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all dark:border-gray-600 peer-checked:bg-red-600"></span>
                    </span>
                  </label>
                </div>
              </div>
              {/* {|FIM| 11/06/2026 às 20:47 - COMPARTILHAMENTO: INSERIR BOTÃO PARA COMPARTILHAR LINK DA DATA ATUAL} */}
              {/* {|FIM| 12/03/2026 às 21:05 - UI: ADICIONAR TOGGLE "OCULTAR INDISPONÍVEIS?" NO CABEÇALHO} */}
              
              {/* {|INICIO| 12/03/2026 às 19:06 - SCROLL DESKTOP: GARANTIR ALTURA E SCROLL APENAS NA LISTA} */}
              <div className="lg:flex-1 lg:min-h-0 lg:flex lg:flex-col lg:overflow-hidden">
                {carregandoAgendaInicial ? (
                  <div className="flex items-center justify-center py-12 lg:flex-1 lg:min-h-0">
                    <div className="flex flex-col items-center space-y-3">
                      <div className="relative">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                        <div className="absolute inset-0 rounded-full border-2 border-red-200 opacity-25"></div>
                      </div>
                      <span className={`${theme === 'dark' ? 'text-dark-secondary' : 'text-gray-600'}`}>
                        {t('common.loading')}...
                      </span>
                    </div>
                  </div>
                ) : slotsToRender.length === 0 ? (
                  <div className="text-center py-12 lg:flex-1 lg:min-h-0">
                    <Clock className={`w-12 h-12 mx-auto mb-3 ${
                      theme === 'dark' ? 'text-dark-muted' : 'text-gray-400'
                    }`} />
                    <p className={`${theme === 'dark' ? 'text-dark-secondary' : 'text-gray-500'}`}>
                      {t('app.noSlotsAvailable')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 sm:space-y-3 lg:space-y-2 lg:flex lg:flex-col lg:flex-1 lg:min-h-0">
                    <div className="space-y-2 sm:space-y-3 lg:space-y-2 lg:flex-shrink-0">
                      {/* Área de boas-vindas após login */}
                      {currentUser && (
                        <div className={`border rounded-lg p-2 sm:p-3 lg:p-2 mb-2 sm:mb-3 lg:mb-2 ${
                          theme === 'dark' 
                            ? 'bg-green-900/20 border-green-700' 
                            : 'bg-green-50 border-green-200'
                        }`}>
                          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-0">
                            <div className="flex items-center space-x-3 w-full sm:w-auto">
                              <UserIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                              <span className={`font-medium text-sm sm:text-base ${
                                theme === 'dark' ? 'text-green-300' : 'text-green-800'
                              }`}>
                                {t('app.welcome')}, {currentUser.nome.toUpperCase()}
                              </span>
                            </div>
                            <div className={`font-medium text-sm sm:text-base flex flex-col items-start sm:items-end w-full sm:w-auto mt-1 sm:mt-0 ${
                              theme === 'dark' ? 'text-green-300' : 'text-green-800'
                            }`}>
                              <span>{t('app.points')}: {currentUser.pontos || 0}</span>
                              {/* {|INICIO| 22/07/2026 às 14:24 - ETAPA 4 AJUSTE: RESTAURAR EXIBICAO DO SALDO DA CONTA ASSINADA} */}
                              {/* Versão anterior (histórico da remoção):
                              {(currentUser.conta_assinada || 0) > 0 && (
                                <span>{t('app.balance')}: {formatCurrency(currentUser.conta_assinada || 0)}</span>
                              )}
                              */}
                              {(currentUser.conta_assinada || 0) > 0 && (
                                <span>{t('app.balance')}: {formatCurrency(currentUser.conta_assinada || 0)}</span>
                              )}
                              {/* {|FIM| 22/07/2026 às 14:24 - ETAPA 4 AJUSTE: RESTAURAR EXIBICAO DO SALDO DA CONTA ASSINADA} */}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Mostrar prompt de login se não estiver logado */}
                      {!currentUser && (
                        <div className={`border rounded-lg p-2 sm:p-3 lg:p-2 mb-2 sm:mb-3 lg:mb-2 ${
                          theme === 'dark' 
                            ? 'bg-red-900/20 border-red-700' 
                            : 'bg-red-50 border-red-200'
                        }`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-3">
                              <UserIcon className="w-5 h-5 text-red-600" />
                              <span className={`font-medium ${
                                theme === 'dark' ? 'text-red-300' : 'text-red-800'
                              }`}>
                                {t('app.loginToReserve')}
                              </span>
                            </div>
                            <button
                              onClick={() => setShowAuthModal(true)}
                              className="flex items-center space-x-2 px-3 py-1.5 lg:py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                            >
                              <span>{t('auth.login')}</span>
                              <UserIcon className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <div className="space-y-2 sm:space-y-3 lg:space-y-2 lg:flex-1 lg:min-h-0 lg:overflow-y-auto lg:pr-1">
                      {/* {|INICIO| 27/02/2026 às 10:00 - AJUSTE DE LAYOUT MOBILE: DISPOSIÇÃO HORIZONTAL E REDUÇÃO DE FONTE/ESPAÇAMENTO} */}
                      {slotsToRender.map((slot) => {
                        // {|INICIO| 28/03/2026 às 00:25 - AGENDA: PADRONIZAR PARSE DE HORA E MOTIVOS}
                        // Versão anterior (histórico):
                        // const availability = checkSlotAvailability(slot, t);
                        const availability = checkSlotAvailability(slot, t, selectedDate);
                        // {|INICIO| 29/03/2026 às 20:00 - AGENDA: IDENTIFICAR BATERIA ABERTA NOS SLOTS}
                        const showOpenBatteryBadge = !availability.isDisabled && isOpenBatterySlot(slot);
                        // {|FIM| 29/03/2026 às 20:00 - AGENDA: IDENTIFICAR BATERIA ABERTA NOS SLOTS}
                        const isLoggedIn = !!currentUser;
                        // {|INICIO| 12/03/2026 às 18:31 - MELHORIA VISUAL DESLOGADO: MANTER CARD LEGÍVEL E TROCAR CTA PARA ENTRAR}
                        const isSlotDisabled = availability.isDisabled;
                        const handleSlotClick = () => {
                          if (isSlotDisabled) return;
                          if (!isLoggedIn) {
                            setShowAuthModal(true);
                            return;
                          }
                          handleReservationSelect(slot);
                        };
                        // {|FIM| 12/03/2026 às 18:31 - MELHORIA VISUAL DESLOGADO: MANTER CARD LEGÍVEL E TROCAR CTA PARA ENTRAR}
                        
                        return (
                          <div
                            key={slot.idagenda}
                            className={`border-2 lg:border rounded-lg p-2 sm:p-3 lg:p-2 transition-all ${
                              isSlotDisabled
                                ? theme === 'dark'
                                  ? 'bg-dark-surface/50 border-dark-border opacity-50 cursor-not-allowed'
                                  : 'bg-gray-50 border-gray-200 opacity-50 cursor-not-allowed'
                                : theme === 'dark'
                                  ? 'bg-dark-surface border-dark-border hover:border-red-500 hover:shadow-md cursor-pointer'
                                  : 'bg-white border-gray-200 hover:border-red-300 hover:shadow-md cursor-pointer'
                            }`}
                            onClick={handleSlotClick}
                          >
                            <div className="flex flex-row items-center justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <h3 className={`font-bold text-sm sm:text-base lg:text-base mb-1 truncate ${
                                  theme === 'dark' ? 'text-dark-primary' : 'text-gray-900'
                                }`}>
                                  {slot.nomeitem}
                                </h3>
                                {/* {|INICIO| 29/03/2026 às 22:58 - UI MOBILE: EVITAR CORTE DOS BADGES (BATERIA/MOTIVO) } */}
                                {/* {|INICIO| 29/03/2026 às 20:17 - UI: FIXAR COLUNAS PARA ALINHAR BADGES (HORÁRIO/MOTIVOS/BATERIA) } */}
                                <div className={`grid grid-cols-[96px_1fr] sm:grid-cols-[96px_140px_1fr] items-center gap-x-3 gap-y-1 text-xs sm:text-sm ${
                                  theme === 'dark' ? 'text-dark-secondary' : 'text-gray-600'
                                }`}>
                                  <span className="flex items-center gap-1 w-[96px] shrink-0">
                                    <Clock className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span>{formatSlotTime(slot, selectedDate, 'pt-BR')}</span>
                                  </span>
                                  <span className="flex items-center gap-1 w-[140px] min-w-0 shrink-0">
                                    <Users className="w-3 h-3 sm:w-4 sm:h-4" />
                                    <span className="truncate">
                                      {availability.statusText === 'INDISPONÍVEL' 
                                        ? t('app.status.indisponível')
                                        : availability.statusText
                                      }
                                    </span>
                                  </span>
                                  <div className="col-span-2 sm:col-span-1 flex flex-wrap items-center justify-start gap-2 min-w-0 sm:min-w-[140px]">
                                    {/* {|INICIO| 29/03/2026 às 20:00 - AGENDA: IDENTIFICAR BATERIA ABERTA NOS SLOTS} */}
                                    {showOpenBatteryBadge && (
                                      <span className="inline-flex items-center whitespace-nowrap rounded-md border border-purple-200 bg-purple-50 px-2 py-0.5 text-[10px] font-semibold leading-none text-purple-700 shadow-sm sm:text-xs dark:border-purple-700 dark:bg-purple-900/20 dark:text-purple-200">
                                        {t('calendar.openBattery')}
                                      </span>
                                    )}
                                    {/* {|FIM| 29/03/2026 às 20:00 - AGENDA: IDENTIFICAR BATERIA ABERTA NOS SLOTS} */}
                                    {availability.reason && (
                                      <>
                                        {/* {|INICIO| 12/03/2026 às 19:54 - UI: DESTACAR MOTIVO EM VERMELHO NOS HORÁRIOS} */}
                                        <span className="inline-flex items-center whitespace-nowrap rounded-md border border-red-200 bg-red-50 px-2 py-0.5 text-[10px] font-semibold leading-none text-red-700 shadow-sm sm:text-xs dark:border-red-700 dark:bg-red-900/20 dark:text-red-200">
                                          ({availability.reason})
                                        </span>
                                        {/* {|FIM| 12/03/2026 às 19:54 - UI: DESTACAR MOTIVO EM VERMELHO NOS HORÁRIOS} */}
                                      </>
                                    )}
                                  </div>
                                </div>
                                {/* {|FIM| 29/03/2026 às 20:17 - UI: FIXAR COLUNAS PARA ALINHAR BADGES (HORÁRIO/MOTIVOS/BATERIA) } */}
                                {/* {|FIM| 29/03/2026 às 22:58 - UI MOBILE: EVITAR CORTE DOS BADGES (BATERIA/MOTIVO) } */}
                              </div>
                              <div className="flex flex-col items-end shrink-0">
                                <p className="text-sm sm:text-lg lg:text-lg font-bold text-green-600 mb-1 sm:mb-2 lg:mb-1">
                                  {formatCurrency(slot.valorliquido)}
                                </p>
                                {isSlotDisabled ? (
                                  <button 
                                    disabled
                                    className={`px-3 py-1.5 lg:px-4 lg:py-1 sm:px-6 sm:py-2 text-xs sm:text-base lg:text-sm rounded-lg cursor-not-allowed ${
                                      theme === 'dark' 
                                        ? 'bg-gray-700 text-gray-400' 
                                        : 'bg-gray-400 text-gray-600'
                                    }`}
                                  >
                                    {t('app.reserve')}
                                  </button>
                                ) : !isLoggedIn ? (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setShowAuthModal(true);
                                    }}
                                    className="flex items-center space-x-2 px-3 py-1.5 lg:px-4 lg:py-1 sm:px-6 sm:py-2 text-xs sm:text-base lg:text-sm bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-md"
                                  >
                                    <span>{t('auth.login')}</span>
                                    <UserIcon className="w-4 h-4" />
                                  </button>
                                ) : (
                                  <button 
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleReservationSelect(slot);
                                    }}
                                    className="px-3 py-1.5 lg:px-4 lg:py-1 sm:px-6 sm:py-2 text-xs sm:text-base lg:text-sm bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-md"
                                  >
                                    {t('app.reserve')}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                        // {|FIM| 28/03/2026 às 00:25 - AGENDA: PADRONIZAR PARSE DE HORA E MOTIVOS}
                      })}
                      {/* {|FIM| 27/02/2026 às 10:00 - AJUSTE DE LAYOUT MOBILE: DISPOSIÇÃO HORIZONTAL E REDUÇÃO DE FONTE/ESPAÇAMENTO} */}
                    </div>
                  </div>
                )}
              </div>
              {/* {|FIM| 12/03/2026 às 19:48 - LISTA HORÁRIOS: COMPACTAR LINHAS NO DESKTOP PARA EXIBIR MAIS ITENS} */}
              {/* {|FIM| 12/03/2026 às 19:06 - SCROLL DESKTOP: GARANTIR ALTURA E SCROLL APENAS NA LISTA} */}
            </div>
          </div>
        </div>
        {/* {|FIM| 12/03/2026 às 20:08 - CALENDÁRIO: AMPLIAR ALTURA PARA OCUPAR ESPAÇO VAZIO (QUADRO VERMELHO)} */}
      </main>
      {/* {|FIM| 12/03/2026 às 20:45 - LAYOUT: REMOVER LIMITE 2XL PARA REDUZIR MARGENS EM TELAS MUITO LARGAS} */}
      {/* {|FIM| 12/03/2026 às 19:41 - LAYOUT: REDUZIR AINDA MAIS MARGENS LATERAIS (REF. QUADRO VERMELHO)} */}
      {/* {|FIM| 12/03/2026 às 19:37 - LAYOUT: REDUZIR MARGENS LATERAIS NO DESKTOP PARA DISTRIBUIR MELHOR O CONTEÚDO} */}
      {/* {|FIM| 12/03/2026 às 19:32 - LAYOUT: RESTAURAR MARGENS LATERAIS (DIREITA/ESQUERDA) CONFORME REFERÊNCIA} */}
      {/* {|FIM| 12/03/2026 às 19:25 - LAYOUT: AUMENTAR LARGURA ÚTIL NO DESKTOP PARA REDUZIR MARGENS LATERAIS} */}
      {/* {|FIM| 12/03/2026 às 19:18 - AJUSTE VISUAL: REDUZIR MARGENS/PADDINGS NAS CAIXAS (CALENDÁRIO E LISTA)} */}
      {/* {|FIM| 12/03/2026 às 18:45 - SCROLL DESKTOP: LIMITAR SCROLL À LISTA DE HORÁRIOS} */}

      {/* Global Spinner */}
      <GlobalSpinner show={globalLoading} />

      {/* Session Timer */}
      <SessionTimer />

      {/* Modals */}
      <Suspense fallback={null}>
        {showAuthModal && (
          <AuthModal
            onClose={() => setShowAuthModal(false)}
            onLogin={handleLogin}
            onOpenRegistration={() => {
              setShowAuthModal(false);
              setShowNovoCadastro(true);
            }}
          />
        )}

        {showReservationModal && selectedAgenda && (
          <EnhancedReservationModal
            isOpen={showReservationModal}
            onClose={() => setShowReservationModal(false)}
            slot={{
              id: selectedAgenda.idagenda.toString(),
              horainicio: new Date(selectedAgenda.horainicio).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
              horatermino: new Date(selectedAgenda.horatermino).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }),
              valorprepago: selectedAgenda.valorliquido || 0,
              valoravista: selectedAgenda.valorliquido || 0,
              disponivel: selectedAgenda.vagas > 0,
              vagas: selectedAgenda.vagas,
              vagas_totais: selectedAgenda.vagas_totais,
              fechado: Boolean(selectedAgenda.fechado),
              parcela_cartao_venda_online: selectedAgenda.parcela_cartao_venda_online,
              parcelamento_venda_online_efetivo: selectedAgenda.parcelamento_venda_online_efetivo,
              /* {|INICIO| 18/06/2026 às 16:39 - PROPAGAR LIMITE EXCLUSIVO DE QUANTIDADE PARA O MODAL DE NOVA RESERVA} */
              permite_somente_quantidade: (selectedAgenda as any)?.permite_somente_quantidade,
              quantidade_maxima_por_cpf_disponivel: (selectedAgenda as any)?.quantidade_maxima_por_cpf_disponivel,
              /* {|FIM| 18/06/2026 às 16:39 - PROPAGAR LIMITE EXCLUSIVO DE QUANTIDADE PARA O MODAL DE NOVA RESERVA} */
              // Comentário (function-level): Preferir mínimo de vagas para bateria fechada vindo do slot
              // Caso o backend inclua esse campo no retorno da agenda, propagate aqui.
              // Se ausente, o modal fará fallback para user.min_vagas_bat_fechada.
              min_vagas_bat_fechada: (selectedAgenda as any)?.min_vagas_bat_fechada,
              nomeitem: selectedAgenda.nomeitem,
              tipo: 'individual', // Default type, can be changed in modal
            }}
            currentUser={currentUser}
            onReservationComplete={() => {
              setShowReservationModal(false);
              loadAvailableSlots(); // Reload slots to update availability
              setShowMyReservations(true); // Redirecionar para "Minhas Reservas" após reserva
            }}
            selectedDate={selectedDate} // Passar a data selecionada
          />
        )}

        {showCheckoutModal && (
          <EnhancedCheckoutModal
            reservation={null}
            currentUser={currentUser}
            onClose={() => setShowCheckoutModal(false)}
            onPaymentSuccess={() => {
              setShowCheckoutModal(false);
              setShowMyReservations(true); // Redirecionar para "Minhas Reservas"
            }}
          />
        )}

        {showMyReservations && currentUser && <MyReservations currentUser={currentUser} onClose={() => setShowMyReservations(false)} />}

        {showMyRaces && currentUser && <MyRaces currentUser={currentUser} onClose={() => setShowMyRaces(false)} />}

        {showConsultarPontos && <ConsultarPontosModal onClose={() => setShowConsultarPontos(false)} />}

        {showTrocarSenha && currentUser && (
          <TrocarSenhaModal
            currentUser={currentUser}
            trocaObrigatoria={Boolean(currentUser.exigir_troca_senha)}
            onClose={() => setShowTrocarSenha(false)}
            onLogout={() => {
              setCurrentUser(null);
              setShowTrocarSenha(false);
              setShowAuthModal(true);
            }}
          />
        )}

        {showNovoCadastro && (
          <NovoCadastroModal
            onClose={() => setShowNovoCadastro(false)}
            onSuccess={() => {
              setShowNovoCadastro(false);
              setShowAuthModal(true);
            }}
          />
        )}

        {showMeuPerfil && currentUser && (
          <MeuPerfilModal
            currentUser={currentUser}
            onClose={() => setShowMeuPerfil(false)}
            onUserUpdate={(updatedUser) => {
              setCurrentUser(updatedUser);
              setShowMeuPerfil(false);
            }}
          />
        )}

        {showParticipantDashboard && currentUser && (
          <EnhancedParticipantDashboard reservation={null} currentUser={currentUser} onClose={() => setShowParticipantDashboard(false)} />
        )}

        {enablePagarmeTest && showPagarmeTest && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-600">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Suite de Testes - Pagar.me</h2>
                <button
                  onClick={() => setShowPagarmeTest(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-4">
                <PagarmeTestSuite />
              </div>
            </div>
          </div>
        )}

        {enableWebhookMonitoring && showWebhookMonitoring && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-7xl w-full max-h-[90vh] overflow-y-auto">
              <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-600">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Monitoramento de Webhooks</h2>
                <button
                  onClick={() => setShowWebhookMonitoring(false)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-4">
                <WebhookMonitoringDashboard />
              </div>
            </div>
          </div>
        )}

        {inviteToken && currentUser && (
          <EnhancedInviteAcceptance
            token={inviteToken}
            onLogin={setCurrentUser}
            onPaymentSuccess={() => setShowMyReservations(true)} // Redirecionar para "Minhas Reservas"
          />
        )}
      </Suspense>

      {/* Modais de teste removidos */}
      {/* {|FIM| 12/03/2026 às 18:58 - SCROLL DESKTOP: TRAVAR BODY E LIMITAR SCROLL AO QUADRO DE HORÁRIOS} */}
    </div>
  );
}

function App() {
  return (
    <ThemeProvider>
      <LanguageProvider>
        <AppContent />
      </LanguageProvider>
    </ThemeProvider>
  );
}

export default App;
